// src/pages/ArtworkDetail.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import LotCreator from "../components/LotCreator";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { inferMediumCategory } from "../utils/mediumCategory";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

import ArtworkSpecificationsTab from "../components/artwork/tabs/ArtworkSpecificationsTab";
import ArtworkProvenanceAcquisitionTab from "../components/artwork/tabs/ArtworkProvenanceAcquisitionTab";
import ArtworkValuationHistory from "../components/artwork/ArtworkValuationHistory";
import ArtworkDocumentsTab from "../components/artwork/tabs/ArtworkDocumentsTab";
import ArtworkLocationTab from "../components/artwork/tabs/ArtworkLocationTab";
import ArtworkLifecycleTab from "../components/artwork/tabs/ArtworkLifecycleTab";
import ArtworkImages from "../components/artwork/ArtworkImages";
import SaleModal from "../components/artwork/modals/SaleModal";
import DonationModal from "../components/artwork/modals/DonationModal";
import LoanModal from "../components/artwork/modals/LoanModal";
import OcrEnrichButton from "../components/artwork/OcrEnrichButton";
import ArtworkDossierExport from "../components/ArtworkDossierExport";
import ArtworkAccessManagement from "../components/artwork/ArtworkAccessManagement";
import { useArtworkThirdPartyAccess } from "../hooks/useArtworkThirdPartyAccess";
import { useLogArtworkViewOnce } from "../hooks/useLogArtworkViewOnce";

import {
  TrendingUp,
  BookOpen,
  Shield,
  Truck,
  Trash2,
  Gift,
  ArrowLeft,
  Edit3,
  MoreVertical,
  Wrench,
  Euro,
  HandCoins,
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  ClipboardList,
  QrCode,
  Building2,
  Users,
  Clock,
  AlertTriangle,
} from "lucide-react";

import QRCodeLib from "qrcode";
import BlockchainCertificate from "../components/BlockchainCertificate";
import { getTechniqueLabel, getTypeOfArtLabel } from "../utils/artkivLabels";
import { openVision } from "../utils/openVision";
import ArtworkReadinessCard from "../components/ArtworkReadinessCard";
import OwnershipHint from "../components/family/OwnershipHint";

// Affichage du médium : on préfère la clé technique canonique (traduite via i18n) au
// texte original (souvent en français). On retombe sur le texte brut uniquement quand
// la clé est absente ou générique/placeholder (sinon on afficherait « Painting » vague
// ou une clé humanisée moche à la place d'une saisie plus précise).
const GENERIC_MEDIUM_KEYS = new Set([
  "", "unknown", "unmatched", "other",
  "painting_generic", "photography_generic", "paper_generic", "sculpture_generic",
  "oil_paint", "acrylic_paint", "print", "paper",
]);
// Clé générique (ex. huile) + saisie libre : hors français, on traduit le type et on garde la saisie entre parenthèses.
const mediumDisplayText = (row, t, lang) => {
  const shown = mediumForDisplay(row);
  const key = row?.medium_normalized_key;
  const raw = String(row?.medium || "").trim();
  if (!String(lang).startsWith("fr") && key && GENERIC_MEDIUM_KEYS.has(key) && key !== "" && raw && shown === raw) {
    const label = getTechniqueLabel(key, t);
    if (label && label.toLowerCase() !== raw.toLowerCase()) return `${label} (${raw})`;
  }
  const label = getTechniqueLabel(shown, t);
  // Saisie sans traduction connue : en français on garde le texte tel qu'il a été écrit (pas de majuscules ajoutées).
  if (String(lang).startsWith("fr") && shown && label.toLowerCase().replace(/[,\s]+/g, " ") === String(shown).toLowerCase().replace(/[,\s]+/g, " ")) return shown;
  return label;
};
const mediumForDisplay = (row = {}) => {
  const key = row.medium_normalized_key;
  return key && !GENERIC_MEDIUM_KEYS.has(key) ? key : (row.medium || key || "");
};

// ✅ Fallback icons (évite crash si une icône n'existe pas dans ta version lucide)
const HandCoinsIcon = typeof HandCoins === "function" ? HandCoins : Euro;
const ClipboardListIcon = typeof ClipboardList === "function" ? ClipboardList : Wrench;
const Building2Icon = typeof Building2 === "function" ? Building2 : Shield;
const CheckCircle2Icon = typeof CheckCircle2 === "function" ? CheckCircle2 : Shield;
const AlertCircleIcon = typeof AlertCircle === "function" ? AlertCircle : Shield;

class LocalBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    console.error(`[ArtworkDetail Boundary] ${this.props.name || ""}`, err, info);
  }

  render() {
    if (this.state.err) {
      return (
        <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-700">
          <div className="font-semibold">
            Un module de la fiche œuvre a crashé
            {this.props.name ? `: ${this.props.name}` : ""}.
          </div>
          <div className="text-sm mt-1">
            {String(this.state.err?.message || this.state.err)}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function QRCodeCanvas({ value, size = 200, level = "H", includeMargin = true }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;

    // Normalise qrcode (CJS/ESM)
    const QR =
      QRCodeLib && typeof QRCodeLib.toCanvas === "function"
        ? QRCodeLib
        : QRCodeLib &&
          QRCodeLib.default &&
          typeof QRCodeLib.default.toCanvas === "function"
        ? QRCodeLib.default
        : null;

    if (!QR) {
      console.warn("[QRCode] toCanvas indisponible", QRCodeLib);
      return;
    }

    try {
      const marginSize = includeMargin ? 4 : 0;
      const ecc = String(level || "H").toUpperCase(); // H/Q/M/L

      QR.toCanvas(
        canvas,
        value,
        {
          width: size,
          margin: marginSize,
          errorCorrectionLevel: ecc,
        },
        (error) => {
          if (error) console.error("[QRCode] Erreur génération:", error);
        }
      );
    } catch (e) {
      console.error("[QRCode] Crash:", e);
    }
  }, [value, size, level, includeMargin]);

  return (
    <canvas
      ref={canvasRef}
      className="mx-auto"
      style={{ maxWidth: "100%", height: "auto" }}
    />
  );
}

// ✅ Debug uniquement en dev (évite bruit / surprises en prod)
if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.log("[ArtworkDetail] components types", {
    ArtworkSpecificationsTab: typeof ArtworkSpecificationsTab,
    ArtworkProvenanceAcquisitionTab: typeof ArtworkProvenanceAcquisitionTab,
    ArtworkDocumentsTab: typeof ArtworkDocumentsTab,
    ArtworkImages: typeof ArtworkImages,
    SaleModal: typeof SaleModal,
    DonationModal: typeof DonationModal,
    BlockchainCertificate: typeof BlockchainCertificate,
    QRCodeCanvas: typeof QRCodeCanvas,
  });
}

function parseMaybeJSON(value, fallback) {
  if (!value) return fallback;
  try {
    if (typeof value === "string") return JSON.parse(value);
    if (typeof value === "object") return value;
    return fallback;
  } catch {
    return fallback;
  }
}

function parseDimensionsString(dimensions) {
  if (!dimensions || typeof dimensions !== "string") return {};

  const cleaned = dimensions
    .toLowerCase()
    .replace(/cm|mm|inches|inch|"/g, "")
    .replace(/×/g, "x")
    .replace(/\s+/g, "")
    .trim();

  if (!cleaned) return {};

  const parts = cleaned
    .split("x")
    .map((p) => parseFloat(p))
    .filter((n) => !Number.isNaN(n));

  const [height, width, depth] = parts;
  return {
    height: height || null,
    width: width || null,
    depth: depth || null,
  };
}

// Gen Z / Millennial widgets (Health / Story / Next Action)

function normalizeDocs(docs) {
  const arr = Array.isArray(docs) ? docs : [];
  // accepte strings, objets {url}, objets {type, name}, etc.
  return arr.filter(Boolean);
}

function countMissingCoreFields({ location_label, acquisitionPrice, docsCount }) {
  const missing = {
    location: !location_label,
    purchase: !(acquisitionPrice != null && Number(acquisitionPrice) > 0),
    docs: docsCount === 0,
  };
  const missingCount = Object.values(missing).filter(Boolean).length;
  return { missing, missingCount };
}

function pickNextAction({ missing, canEdit }) {
  // priorité: docs -> prix -> localisation
  if (!canEdit) return null;

  if (missing.docs) {
    return {
      key: "docs",
      tone: "warn",
      titleKey: "artworkDetail.nextAction.docs.title",
      descKey: "artworkDetail.nextAction.docs.desc",
      ctaKey: "artworkDetail.nextAction.docs.cta",
      tab: "documents",
    };
  }

  if (missing.purchase) {
    return {
      key: "purchase",
      tone: "warn",
      titleKey: "artworkDetail.nextAction.purchase.title",
      descKey: "artworkDetail.nextAction.purchase.desc",
      ctaKey: "artworkDetail.nextAction.purchase.cta",
      // souvent c'est dans edit, mais on te donne un jump simple
      tab: "details",
      edit: true,
    };
  }

  if (missing.location) {
    return {
      key: "location",
      tone: "warn",
      titleKey: "artworkDetail.nextAction.location.title",
      descKey: "artworkDetail.nextAction.location.desc",
      ctaKey: "artworkDetail.nextAction.location.cta",
      tab: "details",
      edit: true,
    };
  }

  return {
    key: "allgood",
    tone: "ok",
    titleKey: "artworkDetail.nextAction.ok.title",
    descKey: "artworkDetail.nextAction.ok.desc",
    ctaKey: "artworkDetail.nextAction.ok.cta",
    tab: "history",
  };
}

function buildStoryEvents({ artwork, historyItems }) {
  const events = [];

  // Acquisition (si date ou source ou prix)
  const acquisition = parseMaybeJSON(artwork?.acquisitionDetails, null) || {};
  const acqDate = acquisition?.date || artwork?.acquisition_date || null;
  const acqSource = acquisition?.source || artwork?.acquisition_source || null;
  const acqType = acquisition?.type || artwork?.acquisition_type || null;
  const acqPrice = acquisition?.price ?? artwork?.acquisition_price ?? null;

  if (acqDate || acqSource || acqType || acqPrice) {
    events.push({
      kind: "acquisition",
      occurred_at: acqDate || null,
      titleKey: "artworkDetail.story.acquisition",
      meta: [acqType, acqSource].filter(Boolean).join(" · "),
    });
  }

  // Provenance (si tableau)
  const prov = Array.isArray(artwork?.provenance) ? artwork.provenance : [];
  for (const p of prov.slice(0, 4)) {
    const date =
      p?.date ||
      p?.year ||
      p?.from ||
      p?.start_date ||
      p?.occurred_at ||
      null;

    const label =
      p?.title || p?.event || p?.location || p?.owner || p?.text || null;

    if (date || label) {
      events.push({
        kind: "provenance",
        occurred_at: date || null,
        titleKey: "artworkDetail.story.provenance",
        meta: label || "",
      });
    }
  }

  // Tri: dates connues d'abord, sinon en bas
  const toTs = (d) => {
    if (!d) return null;
    const t = Date.parse(d);
    return Number.isFinite(t) ? t : null;
  };

  events.sort((a, b) => {
    const ta = toTs(a.occurred_at);
    const tb = toTs(b.occurred_at);
    if (ta == null && tb == null) return 0;
    if (ta == null) return 1;
    if (tb == null) return -1;
    return tb - ta;
  });

  return events.slice(0, 8);
}

function ArtworkHealthCheckBar({ t, missing, missingCount, docsCount, isLost, lossStatusHref }) {
  // 🛡️ GARDE: Vérifier que t est une fonction
  if (!t || typeof t !== "function") return null;

  const itemClass = (isMissing) =>
    `inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs border ${
      isMissing
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-emerald-200 bg-emerald-50 text-emerald-900"
    }`;

  return (
    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {missingCount === 0 && !isLost ? (
            <CheckCircle2Icon className="w-5 h-5 text-emerald-600" />
          ) : (
            <AlertCircleIcon className="w-5 h-5 text-amber-600" />
          )}
          <div className="text-sm font-medium text-gray-900">
            {missingCount === 0 && !isLost
              ? t("artworkDetail.health.ok")
              : t("artworkDetail.health.todo", { count: missingCount + (isLost ? 1 : 0) })}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isLost && (
            <a
              href={lossStatusHref || "#"}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 transition-colors"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Sinistre actif
            </a>
          )}

          <span className={itemClass(missing.docs)}>
            <ClipboardListIcon className="w-4 h-4" />
            {missing.docs
              ? t("artworkDetail.health.docsMissing")
              : t("artworkDetail.health.docsOk", { count: docsCount })}
          </span>

          <span className={itemClass(missing.purchase)}>
            <HandCoinsIcon className="w-4 h-4" />
            {missing.purchase
              ? t("artworkDetail.health.purchaseMissing")
              : t("artworkDetail.health.purchaseOk")}
          </span>

          <span className={itemClass(missing.location)}>
            <Truck className="w-4 h-4" />
            {missing.location
              ? t("artworkDetail.health.locationMissing")
              : t("artworkDetail.health.locationOk")}
          </span>
        </div>
      </div>
    </div>
  );
}

function ArtworkNextActionCard({ t, action, onGoTab, onGoEdit }) {
  // 🛡️ GARDE: Vérifier que t est une fonction
  if (!t || typeof t !== "function") return null;

  if (!action) return null;

  const isOk = action.tone === "ok";
  return (
    <div
      className={`rounded-xl border p-5 shadow-sm ${
        isOk ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
      }`}
    >
      <div className="flex items-start gap-3">
        {isOk ? (
          <CheckCircle2Icon className="w-5 h-5 text-emerald-700 mt-0.5" />
        ) : (
          <AlertCircleIcon className="w-5 h-5 text-amber-700 mt-0.5" />
        )}

        <div className="flex-1">
          <div className="font-semibold text-gray-900">{t(action.titleKey)}</div>
          <div className="mt-1 text-sm text-gray-700">{t(action.descKey)}</div>

          <div className="mt-4 flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => {
                if (action.edit) onGoEdit?.();
                else onGoTab?.(action.tab);
              }}
              className="px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition"
            >
              {t(action.ctaKey)}
            </button>

            {action.tab && action.tab !== "details" && !action.edit && (
              <button
                type="button"
                onClick={() => onGoTab?.(action.tab)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-800 text-sm font-medium hover:bg-white/60 transition"
              >
                {t("artworkDetail.nextAction.viewTab")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ✅ NOUVEAU COMPOSANT : Historique avec onglets
function HistoryTabContent({ t, recordItems, recordLoading, currentUserId, locale }) {
  // ✅ TOUS LES HOOKS EN PREMIER (sans aucune condition avant)
  const [viewMode, setViewMode] = useState("third_party"); // "third_party" | "all"
  const [showAll, setShowAll] = useState(false);

  // Filtre les items selon le mode
  const filteredItems = useMemo(() => {
    if (!recordItems || !Array.isArray(recordItems)) return [];
    if (viewMode === "all") return recordItems;
    
    // Mode "third_party" : exclut les consultations du propriétaire
    return recordItems.filter((r) => {
      // Si pas de currentUserId, on garde tout
      if (!currentUserId) return true;
      
      // Si c'est une consultation et que l'acteur est le propriétaire, on filtre
      if (r.action === "Consultation" && r.actor_id === currentUserId) {
        return false;
      }
      return true;
    });
  }, [recordItems, viewMode, currentUserId]);


const premiumActionLabel = (r) => {
  const raw = String(r?.action || "").trim();
  const key = raw.toLowerCase();

  const map = {
    consultation: "artworkDetail.history.actionViewed",
    view: "artworkDetail.history.actionViewed",
    read: "artworkDetail.history.actionViewed",
    modification: "artworkDetail.history.actionUpdated",
    edit: "artworkDetail.history.actionUpdated",
    update: "artworkDetail.history.actionUpdated",
    "ajout document": "artworkDetail.history.actionDocumentAdded",
    document: "artworkDetail.history.actionDocumentAdded",
    upload_document: "artworkDetail.history.actionDocumentAdded",
    "ajout image": "artworkDetail.history.actionImageAdded",
    image: "artworkDetail.history.actionImageAdded",
    invitation: "artworkDetail.history.actionShared",
    partage: "artworkDetail.history.actionShared",
    invite: "artworkDetail.history.actionShared",
    révocation: "artworkDetail.history.actionRevoked",
    revoke: "artworkDetail.history.actionRevoked",
  };

  return map[key]
    ? t(map[key])
    : raw
    ? t("artworkDetail.history.actionGenericNamed", { action: raw })
    : t("artworkDetail.history.actionGeneric");
};

  // Limite à 10 si showAll = false
  const displayedItems = showAll ? filteredItems : filteredItems.slice(0, 10);
  const hasMore = filteredItems.length > 10;

  // 🛡️ GARDE APRÈS TOUS LES HOOKS (si t n'est pas disponible, on affiche un fallback simple)
  if (!t || typeof t !== "function") {
    return (
      <div className="text-sm text-gray-500 p-4">
        {t("artworkDetail.history.loadingFallback")}
      </div>
    );
  }

  if (recordLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mr-3" />
        {t("artworkDetail.history.loading")}
      </div>
    );
  }

  return (
    <div className="text-sm">
      {/* Onglets de filtrage */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-6">
          <button
            onClick={() => setViewMode("third_party")}
            className={`pb-3 px-1 border-b-2 transition ${
              viewMode === "third_party"
                ? "border-black text-black font-medium"
                : "border-transparent text-gray-500 hover:text-black"
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>
                {t("artworkDetail.history.thirdPartyAccess")}
              </span>
              {viewMode === "third_party" && filteredItems.length > 0 && (
                <span className="ml-1.5 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                  {filteredItems.length}
                </span>
              )}
            </div>
          </button>

          <button
            onClick={() => setViewMode("all")}
            className={`pb-3 px-1 border-b-2 transition ${
              viewMode === "all"
                ? "border-black text-black font-medium"
                : "border-transparent text-gray-500 hover:text-black"
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                {t("artworkDetail.history.fullHistory")}
              </span>
              {viewMode === "all" && recordItems.length > 0 && (
                <span className="ml-1.5 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                  {recordItems.length}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Liste des événements */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            {viewMode === "third_party" ? (
              <Users className="w-8 h-8 text-gray-400" />
            ) : (
              <Clock className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <p className="font-medium mb-1">
            {viewMode === "third_party"
              ? t("artworkDetail.history.noThirdParty")
              : t("artworkDetail.history.empty")}
          </p>
          <p className="text-xs text-gray-400">
            {viewMode === "third_party"
              ? t("artworkDetail.history.noThirdPartyDesc")
              : t("artworkDetail.history.emptyDesc")}
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-3">
            {displayedItems.map((r, idx) => {
              const isOwner = r.actor_id === currentUserId;
              
              return (
                <li
                  key={idx}
                  className={`rounded-lg border p-4 transition ${
                    isOwner
                      ? "border-gray-200 bg-gray-50/50"
                      : "border-blue-100 bg-blue-50/30 hover:bg-blue-50/50"
                  }`}
                >
<div className="flex flex-wrap items-center gap-2 mb-2">
  {!isOwner && (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
      <Users className="w-3 h-3" />
      {t("artworkDetail.history.thirdPartyBadge")}
    </span>
  )}

  <span className="font-medium text-gray-900">
    {isOwner
      ? t("artworkDetail.history.actorYou")
      : r.actor_name || t("artworkDetail.history.actorUnknown")}
  </span>

  <span className="text-gray-400">·</span>
  <span className="text-gray-600">{premiumActionLabel(r)}</span>
</div>

<div className="text-xs text-gray-500">
  {new Date(r.created_at).toLocaleString(locale || undefined, {
    dateStyle: "long",
    timeStyle: "short",
  })}
</div>

                  {Array.isArray(r.payload?.changes) && r.payload.changes.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {r.payload.changes.slice(0, 8).map((c, i) => (
                        <div
                          key={i}
                          className="text-xs bg-white border border-gray-200 rounded p-2"
                        >
                          <div className="font-medium text-gray-800">{c.field}</div>
                          <div className="text-gray-600">
                            <span className="text-gray-500">{t("artworkDetail.history.before")}:</span>{" "}
                            {String(c.old ?? "—")}
                          </div>
                          <div className="text-gray-600">
                            <span className="text-gray-500">{t("artworkDetail.history.after")}:</span>{" "}
                            {String(c.new ?? "—")}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Bouton "Voir plus" */}
          {!showAll && hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAll(true)}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-gray-300 hover:bg-gray-50 transition text-sm font-medium"
              >
                <Clock className="w-4 h-4" />
                {t("artworkDetail.history.showAll", {
                  count: filteredItems.length - 10,
                })}
              </button>
            </div>
          )}

          {showAll && hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAll(false)}
                className="text-sm text-gray-500 hover:text-gray-700 transition"
              >
                {t("artworkDetail.history.showLess")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Composant Skeleton AVANT le export default function pour afficher en avance
function ArtworkDetailSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header minimaliste */}
      <div className="border-b border-gray-100 bg-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-full animate-pulse" />
            <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Grid principal */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Image skeleton */}
          <div className="aspect-square bg-gray-50 rounded-2xl border border-gray-100 animate-pulse" />

          {/* Infos skeleton */}
          <div className="space-y-6">
            {/* Titre */}
            <div className="space-y-3">
              <div className="h-8 bg-gray-100 rounded w-3/4 animate-pulse" />
              <div className="h-6 bg-gray-100 rounded w-1/2 animate-pulse" />
            </div>

            {/* Métadonnées */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-1/4 animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-2/5 animate-pulse" />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <div className="h-10 w-32 bg-gray-100 rounded-full animate-pulse" />
              <div className="h-10 w-32 bg-gray-100 rounded-full animate-pulse" />
            </div>
          </div>
        </div>

        {/* Tabs skeleton */}
        <div className="flex gap-4 border-b border-gray-200 mb-6 overflow-x-auto">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-10 w-24 bg-gray-100 rounded-t animate-pulse flex-shrink-0"
            />
          ))}
        </div>

        {/* Content blocks skeleton */}
        <div className="space-y-4">
          <div className="h-32 bg-gray-50 rounded-xl border border-gray-100 animate-pulse" />
          <div className="h-32 bg-gray-50 rounded-xl border border-gray-100 animate-pulse" />
        </div>
      </div>
    </div>
  );
}


// ── Composant : Œuvres du même lot ─────────────────────────────────────────
// Miniature ultra-légère via Supabase Storage Transform.
// Important : le lot peut contenir 150+ fiches, donc on évite les 80×80 inutiles.
const getThumbUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    u.searchParams.set("width", "32");
    u.searchParams.set("height", "32");
    u.searchParams.set("quality", "45");
    u.searchParams.set("resize", "cover");
    return u.toString();
  } catch {
    return url;
  }
};

function LotSiblings({ artworkId, lotId, lotTitleFallback, t, canEdit, collectionId, clientId, onDuplicate, duplicating }) {
  const [siblings, setSiblings] = React.useState([]);
  const [lotTitle, setLotTitle] = React.useState("");
  const [lotData,  setLotData]  = React.useState(null);
  const [editOpen, setEditOpen] = React.useState(false);

  React.useEffect(() => {
    if (!lotId) return;
    // Charger les autres œuvres du même lot — silencieux si RLS bloque
    Promise.all([
      supabase.from("artworks")
        .select("id, title, inventory_number, images, image_url")
        .eq("lot_id", lotId),
      supabase.from("v_artworks_shared_with_me")
        .select("id, title, inventory_number, images, image_url")
        .eq("lot_id", lotId),
    ]).then(([own, shared]) => {
      const all = [...(own.data || []), ...(shared.data || [])];
      const unique = all.filter((a, i, arr) => arr.findIndex(b => b.id === a.id) === i);
      const nextSiblings = unique.filter(a => a.id !== artworkId);
      setSiblings(nextSiblings);
      // Après chargement réel : replier automatiquement les gros lots.
      // Avant, siblings.length vaut 0 au premier render, donc le lot restait ouvert.
      if (nextSiblings.length > 25) setCollapsed(true);
    }).catch(() => {});
    // Charger le titre du lot — essai direct puis via artwork.lot (déjà jointé)
   supabase.from("lots")
  .select("id, title, artist, technique, total_copies, edition_info, reference_image_url, pricing_mode, global_price")
  .eq("id", lotId).single()
      .then(({ data, error }) => {
        if (data && !error) {
          setLotData(data);
          setLotTitle(data.title + (data.total_copies ? ` — ${data.total_copies} ex.` : "") + (data.edition_info ? ` (${data.edition_info})` : ""));
        }
        // Si RLS bloque (expert), le titre viendra du badge via artwork.lot?.title
      }).catch(() => {});
  }, [lotId, artworkId]);

  const PAGE_SIZE = 25;
  const [lotPage, setLotPage] = React.useState(0);
  // Replié après chargement réel si lot large, pour ne pas bloquer la fiche.
  const [collapsed, setCollapsed] = React.useState(false);

  if (siblings.length === 0) return null;

  const totalPages = Math.ceil(siblings.length / PAGE_SIZE);
  const pageSiblings = siblings.slice(lotPage * PAGE_SIZE, (lotPage + 1) * PAGE_SIZE);

  return (
    <>
    <div id="lot-section" className="mb-6 border border-[#c9a961]/30 rounded-xl p-4 bg-[#fffbf2]">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#c9a961] hover:opacity-75 transition-opacity"
          style={{ fontFamily: "system-ui", background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          <span>
            ▣ {lotTitle || lotTitleFallback || t("artworkDetail.lot.fallbackName")} — {t(
              siblings.length + 1 === 1
                ? "artworkDetail.lot.copySingular"
                : "artworkDetail.lot.copyPlural",
              { count: siblings.length + 1 }
            )}
          </span>
          <span style={{ fontSize: 10, opacity: 0.7 }}>
            {collapsed
              ? `▼ ${t("artworkDetail.lot.show")}`
              : `▲ ${t("artworkDetail.lot.collapse")}`}
          </span>
        </button>
        {canEdit && lotData && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDuplicate}
              disabled={duplicating}
              className="text-xs text-gray-500 border border-gray-200 px-3 py-1 rounded hover:border-[#c9a961] hover:text-[#c9a961] transition-colors disabled:opacity-50"
              style={{ fontFamily: "system-ui" }}
              title={t("artworkDetail.lot.duplicateTitle")}
            >
              {duplicating ? "…" : t("artworkDetail.lot.duplicate")}
            </button>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="text-xs text-[#c9a961] border border-[#c9a961]/50 px-3 py-1 rounded hover:bg-[#c9a961]/10 transition-colors"
              style={{ fontFamily: "system-ui" }}
            >
              {t("artworkDetail.lot.edit")}
            </button>
          </div>
        )}
      </div>

      {/* Onglets + vignettes — masqués si replié */}
      {!collapsed && (
        <>
      {/* Onglets de pagination — affichés seulement si > 25 siblings */}
      {totalPages > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Array.from({ length: totalPages }, (_, i) => {
            const from = i * PAGE_SIZE + 1;
            const to = Math.min((i + 1) * PAGE_SIZE, siblings.length);
            return (
              <button
                key={i}
                type="button"
                onClick={() => setLotPage(i)}
                className="text-xs px-3 py-1 rounded border transition-colors"
                style={{
                  fontFamily: "system-ui",
                  borderColor: lotPage === i ? "#c9a961" : "#e5e3df",
                  background:  lotPage === i ? "#c9a961" : "#fff",
                  color:       lotPage === i ? "#fff"    : "#888",
                  fontWeight:  lotPage === i ? 600       : 400,
                }}
              >
                {from}–{to}
              </button>
            );
          })}
        </div>
      )}

      {/* Vignettes de la page courante uniquement */}
      <div className="flex flex-wrap gap-1.5">
        {pageSiblings.map(s => {
          const img = (() => {
            try {
              const imgs = Array.isArray(s.images) ? s.images : JSON.parse(s.images || "[]");
              const p = imgs.find(i => i.isPrimary) || imgs[0];
              return p?.url || p?.path || s.image_url || null;
            } catch { return s.image_url || null; }
          })();
          return (
            <a
              key={s.id}
              href={`/artwork/${s.id}`}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-[#c9a961]/25 bg-white hover:border-[#c9a961] transition text-xs"
            >
              {img && <img src={getThumbUrl(img)} alt="" loading="lazy" decoding="async" className="w-4 h-4 rounded object-cover" />}
              <span className="text-gray-700 text-[11px] leading-none">{s.inventory_number || s.title || "—"}</span>
            </a>
          );
        })}
      </div>
        </>
      )}
    </div>
    {editOpen && lotData && collectionId && (
      <LotCreator
        existingLot={lotData}
        collectionId={collectionId}
        clientId={clientId}
        onClose={() => setEditOpen(false)}
        onCreated={() => { setEditOpen(false); window.location.reload(); }}
      />
    )}
    </>
  );
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------
export default function ArtworkDetail() {
  // ========================================
  // 1. TOUS LES HOOKS EN PREMIER (ordre strict)
  // ========================================
  const params = useParams();
  const navigate = useNavigate();
  const { t: _t, i18n } = useTranslation();
  const { user, profile, loading: authLoading, profileLoading } = useAuth();

  // États
  const [actionsOpen, setActionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [isLoading, setIsLoading] = useState(true);
  const [artwork, setArtwork] = useState(null);
  const [, setError] = useState(null);
  const [, setNotFoundId] = useState(null);
  const [isShared, setIsShared] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [artworkCollection, setArtworkCollection] = useState(null);
  const [ownershipHolders, setOwnershipHolders] = useState({ bareOwnerName: "", usufructName: "" });
  const [collabPerms, setCollabPerms] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showLossModal, setShowLossModal] = useState(false);
  const [lossForm, setLossForm] = useState({ loss_type: "vol", notes: "", declared_value: "", police_report: false, insurance_claim: false });
  const [submittingLoss, setSubmittingLoss] = useState(false);
  const [lossRecord, setLossRecord] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [recordItems, setRecordItems] = useState([]);
  const [recordLoading, setRecordLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [lotCreatorOpen, setLotCreatorOpen] = useState(false);
  const ocrLauncherRef = useRef(null);

  // ── Visuel de référence du lot (sérigraphies, multiples) ────────────────
  // L'image est stockée dans le bucket 'documents' sous lots/{lotId}/reference.{ext}
  // et exposée via lots.reference_image_url. Permet de partager le même visuel
  // sur les N exemplaires du lot quand certaines œuvres n'ont pas leur propre photo.
  const lotRefInputRef = useRef(null);
  const [lotRefUploading, setLotRefUploading] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [applyToAllResult, setApplyToAllResult] = useState(null); // { count, errors }

  // Memo
  const safeTags = useMemo(() => {
    if (!artwork || !artwork.tags) return [];
    if (Array.isArray(artwork.tags)) return artwork.tags;

    if (typeof artwork.tags === "string") {
      return artwork.tags
        .split(/[;,]/)
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artwork?.tags]);

  // ========================================
  // 2. CALCULS / VARIABLES DÉRIVÉES (après tous les hooks)
  // ========================================
  const artworkId = params.artworkId || params.id || null;

  // ✅ t stable (évite rerenders + deps bizarres)
  const t = useMemo(() => {
    if (!_t || typeof _t !== "function") {
      return (key, opts) => {
        console.warn("[ArtworkDetail] Translation not ready:", key);
        return (opts && opts.defaultValue) || key;
      };
    }

    return (key, opts) => {
      try {
        if (typeof _t !== "function") return (opts && opts.defaultValue) || key;
        return _t(key, opts);
      } catch (e) {
        console.warn("[ArtworkDetail] Translation error:", key, e);
        return (opts && opts.defaultValue) || key;
      }
    };
  }, [_t]);

  // Plan
  const planRaw = String(profile?.plan || "standard").trim().toLowerCase();
  const hasPaidPlanForAI = ["premium", "art_advisor_premium", "family_office"].includes(planRaw)
    || collabPerms?.can_access_ai === true;
  const usesAI = !!profile?.uses_ai;
  const hasAIConsent = !!profile?.ai_signed_at;
  const canUseAI = hasPaidPlanForAI && usesAI && hasAIConsent;

  // Images
  const images = Array.isArray(artwork?.images) ? artwork.images : [];
  const activeImage =
    images.length > 0
      ? typeof images[activeImageIndex] === "string"
        ? images[activeImageIndex]
        : images[activeImageIndex]?.url || images[activeImageIndex]?.path || null
      : null;

  // ========================================
  // 3. HOOKS DÉDIÉS (log view + accès tiers)
  // ========================================
  // Log de consultation — activé pour les tiers (expert, partagé) uniquement
  // Le propriétaire ne se logue pas lui-même pour ne pas polluer l'historique
  useLogArtworkViewOnce({
    artworkId: artwork?.id,
    enabled: !!artwork?.id && isShared, // isShared = true quand Sophie consulte la fiche de Marie
    source: "artwork_detail",
  });

  // ✅ Ne charge l'accès tiers que quand l'onglet est ouvert (moins d'appels, moins d'erreurs)
  const shouldLoadAccess = !!artwork?.id && isOwner && activeTab === "access";
  const { items: accessItems, loading: accessLoading, refetch: refetchAccess } = useArtworkThirdPartyAccess({
     artworkId: artwork?.id,
     enabled: shouldLoadAccess,
   });

  // ========================================
  // 4. useEffect
  // ========================================

  // Log de debug
  useEffect(() => {
    console.log("[ArtworkDetail] 📊 État auth:", {
      authLoading,
      profileLoading,
      hasUser: !!user,
      hasProfile: !!profile,
      artworkId,
    });
  }, [authLoading, profileLoading, user, profile, artworkId]);

  // ----------------------------------------------------------------
  // Loading - principal
  // ----------------------------------------------------------------
  useEffect(() => {
    // Guard 1: pas d'ID
    if (!artworkId) {
      setIsLoading(false);
      setNotFoundId("undefined");
      return;
    }

    // Guard 2: on attend SEULEMENT que authLoading soit terminé
    // ✅ ON NE BLOQUE PLUS SUR profileLoading
    if (authLoading) {
      console.log("[ArtworkDetail] ⏳ Attente fin authLoading...");
      setIsLoading(true);
      return;
    }

    // Guard 3: vraiment pas de user = pas connecté
    if (!user) {
      console.log("[ArtworkDetail] ❌ Pas d'utilisateur connecté");
      setIsLoading(false);
      setError(t("artworkDetail.loginRequired"));
      return;
    }

    // ✅ ICI on a user, on peut charger l'œuvre même si profile n'est pas encore là
    console.log("[ArtworkDetail] ✅ User ready, chargement de l'œuvre...");

    let cancelled = false;

    async function load() {
      try {
        setIsLoading(true);
        setError(null);
        console.log("[ArtworkDetail] 🔍 Fetch artwork ID:", artworkId);

        // 1. Lecture sécurisée : la vue SQL décide si l'utilisateur a accès.
        // IMPORTANT : ne pas filtrer par client_id ici, sinon un expert/collaborateur autorisé
        // ne verra jamais l'œuvre d'un propriétaire.
        let { data: aw, error: errAw } = await supabase
          .from("artworks_secure")
          .select("*")
          .eq("id", artworkId)
          .maybeSingle();

        if (errAw) throw errAw;

        let shared = false;
        let ownerId = aw?.client_id || user.id;
        let accessCanEdit = !!aw && aw.client_id === user.id;

        // 2. Si l'œuvre est visible via artworks_secure mais n'appartient pas à l'utilisateur,
        // récupérer les droits fins sur la collection pour réactiver les sous-menus expert.
        if (aw && aw.client_id !== user.id && aw.collection_id) {
          shared = true;

          const { data: permRow, error: permErr } = await supabase
            .from("collection_permissions")
            .select("can_view, can_edit, can_view_financial, can_view_location, can_view_documents, can_view_provenance, can_view_transmission, can_view_private_notes")
            .eq("collection_id", aw.collection_id)
            .eq("grantee_id", user.id)
            .maybeSingle();

          if (permErr) throw permErr;

          accessCanEdit = !!permRow?.can_edit;
          aw.can_view = permRow?.can_view ?? true;
          aw.can_edit = !!permRow?.can_edit;
          aw._canEdit = !!permRow?.can_edit;
          aw._permissions = permRow || {};

          if (!cancelled) {
            setCollabPerms({
              can_view: permRow?.can_view ?? true,
              can_edit: !!permRow?.can_edit,
              can_view_financial: !!permRow?.can_view_financial,
              can_view_location: !!permRow?.can_view_location,
              can_view_documents: !!permRow?.can_view_documents,
              can_view_provenance: !!permRow?.can_view_provenance,
              can_view_transmission: !!permRow?.can_view_transmission,
              can_view_private_notes: !!permRow?.can_view_private_notes,
            });
          }
        }

        // 2 bis. Si la vue a renvoyé une location_id autorisée, charger le libellé séparément.
        // PostgREST ne sait pas toujours inférer les relations depuis une vue.
        if (aw?.location_id) {
          const { data: locationRow, error: locationErr } = await supabase
            .from("locations")
            .select("id, name, address")
            .eq("id", aw.location_id)
            .maybeSingle();

          if (!locationErr && locationRow) {
            aw.locations = locationRow;
            aw.location = locationRow;
          }
        }

        // 3. Vraiment pas trouvé
        if (!aw) {
          console.log("[ArtworkDetail] ❌ Œuvre non trouvée");
          if (!cancelled) {
            setNotFoundId(artworkId);
            setIsLoading(false);
          }
          return;
        }

        console.log("[ArtworkDetail] ✅ Œuvre chargée:", aw.title || aw.id);

        // 4. Parse et normalise les données
        const rawSpecs = parseMaybeJSON(aw.specifications, {});
        const jsonDims = rawSpecs?.dimensions || {};
        const parsedDims = parseDimensionsString(
          aw.dimensions || [aw.height || "", aw.width || "", aw.depth || ""].join("x")
        );

        // ARTKIV : SQL = source de vérité.
        // specifications.dimensions est un miroir d import/OCR/XLS, jamais prioritaire.
        const dims = {
          ...jsonDims,
          unit: aw.dimensions_unit || aw.unit || jsonDims.unit || "cm",
          height: aw.dimensions_height ?? aw.height ?? parsedDims.height ?? jsonDims.height ?? "",
          width: aw.dimensions_width ?? aw.width ?? parsedDims.width ?? jsonDims.width ?? "",
          depth: aw.dimensions_depth ?? aw.depth ?? parsedDims.depth ?? jsonDims.depth ?? "",
        };

        const normalizedSpecs = {
          ...rawSpecs,
          dimensions: dims,
        };

        const normalized = {
          ...aw,
          images: parseMaybeJSON(aw.images, aw.images || []),
          documents: parseMaybeJSON(aw.documents, aw.documents || []),
          provenance: parseMaybeJSON(aw.provenance, []),
          specifications: normalizedSpecs,
          can_edit: Boolean(aw.can_edit ?? aw._canEdit ?? accessCanEdit),
          can_view: Boolean(aw.can_view ?? true),
          _canEdit: Boolean(aw._canEdit ?? aw.can_edit ?? accessCanEdit),
        };

        // Charger la collection pour connaître collection_type et legal_status
        if (aw.collection_id) {
          const { data: col } = await supabase
            .from("collections")
            .select("id, collection_type, legal_status")
            .eq("id", aw.collection_id)
            .maybeSingle();
          if (!cancelled && col) setArtworkCollection(col);
        }

        if (!cancelled) {
          setArtwork(normalized);

          setIsShared(shared);
          setIsOwner(!shared && ownerId === user.id);
          setError(null);
          setIsLoading(false);

          console.log("[ArtworkDetail] 🎉 Affichage prêt!");
        }
      } catch (e) {
        console.error("[ArtworkDetail] ❌ Erreur chargement:", e);
        if (!cancelled) {
          setError(e.message || "Erreur de chargement");
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [artworkId, user, authLoading, t]);

  // ----------------------------------------------------------------
  // Structure juridique (usufruit / nue-propriété) - noms des bénéficiaires
  // ----------------------------------------------------------------
  useEffect(() => {
    const bareOwnerId = artwork?.bare_owner_id;
    const usufructId = artwork?.usufruct_beneficiary_id;
    if (!bareOwnerId && !usufructId) {
      setOwnershipHolders({ bareOwnerName: "", usufructName: "" });
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = [bareOwnerId, usufructId].filter(Boolean);
      const { data } = await supabase
        .from("beneficiaries")
        .select("id, name")
        .in("id", ids);
      if (cancelled || !data) return;
      const byId = Object.fromEntries(data.map((b) => [b.id, b.name]));
      setOwnershipHolders({
        bareOwnerName: byId[bareOwnerId] || "",
        usufructName: byId[usufructId] || "",
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [artwork?.bare_owner_id, artwork?.usufruct_beneficiary_id]);

  // ----------------------------------------------------------------
  // Historique fiche (modifs) - owner only
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!artwork?.id) return;
    if (!isOwner) return; // Historique owner uniquement
    if (activeTab !== "history") return; // Ne charge que si l'onglet est actif

    let cancelled = false;

    async function loadHistory() {
      try {
        setRecordLoading(true);

        // ✅ MÉTHODE 1 : Requête directe (rapide si RLS OK)
        let { data: logs, error: logsErr } = await supabase
          .from("artwork_access_log")
          .select("*")
          .eq("artwork_id", artwork.id)
          .order("accessed_at", { ascending: false });

        // ✅ MÉTHODE 2 : Si erreur RLS, utiliser la fonction Postgres
        if (logsErr && (logsErr.code === "42501" || logsErr.message?.includes("permission"))) {
          console.warn("[ArtworkDetail] ⚠️ RLS bloque, utilisation de la fonction RPC...");
          
          const { data: rpcLogs, error: rpcErr } = await supabase
            .rpc('get_artwork_access_history', { p_artwork_id: artwork.id });
          
          if (rpcErr) {
            console.error("[ArtworkDetail] ❌ Erreur RPC:", rpcErr);
            if (!cancelled) {
              setRecordItems([]);
              setRecordLoading(false);
            }
            return;
          }
          
          logs = rpcLogs;
          logsErr = null;
        }

        // Si erreur persistante
        if (logsErr) {
          console.error("[ArtworkDetail] Erreur chargement historique:", logsErr);
          console.error("[ArtworkDetail] Message:", logsErr.message);
          console.error("[ArtworkDetail] Code:", logsErr.code);
          
          if (!cancelled) {
            setRecordItems([]);
            setRecordLoading(false);
          }
          return;
        }

        // Si pas de données, retourner un tableau vide
        if (!logs || logs.length === 0) {
          console.log("[ArtworkDetail] ℹ️ Aucun log d'accès trouvé pour cette œuvre");
          if (!cancelled) {
            setRecordItems([]);
            setRecordLoading(false);
          }
          return;
        }

        // Transforme les logs en format attendu par HistoryTabContent
        const items = logs.map((log) => ({
          id: log.id,
          action: log.action === "view" ? "Consultation" : log.action,
          actor_id: log.viewer_id,
          actor_name: log.viewer_name || "Utilisateur inconnu",
          actor_role: log.viewer_role?.toUpperCase() || "USER",
          created_at: log.accessed_at,
          source: log.source || "artwork_detail",
          payload: {
            note: log.note || null,
            user_agent: log.user_agent || null,
            ip_address: log.ip_address || null,
          },
        }));

        if (!cancelled) {
          setRecordItems(items);
          setRecordLoading(false);
          console.log(`[ArtworkDetail] ✅ Chargé ${items.length} événements d'historique`);
        }
      } catch (e) {
        console.error("[ArtworkDetail] ❌ Crash chargement historique:", e);
        if (!cancelled) {
          setRecordItems([]);
          setRecordLoading(false);
        }
      }
    }

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [artwork?.id, isOwner, activeTab]);

  // Badge perte / vol / sinistre
  // Important : en accès expert ou collaborateur, on filtre sur le propriétaire réel
  // de l'œuvre (artwork.client_id), jamais sur l'utilisateur connecté.
  useEffect(() => {
    if (!artwork?.id) {
      setLossRecord(null);
      return;
    }

    let cancelled = false;

    async function loadLossRecord() {
      const ownerClientId = artwork.client_id || artwork.owner_id || null;
      const baseSelect = `
        id,
        client_id,
        artwork_id,
        artwork_title,
        artist,
        inventory_number,
        loss_type,
        loss_date,
        tax_year,
        declared_value,
        purchase_price,
        currency,
        police_report,
        police_ref,
        insurance_claim,
        insurance_ref,
        indemnity,
        location_at_loss,
        notes,
        documents,
        recovered_at
      `;

      try {
        const queries = [];

        // Source normale : perte liée directement à l'œuvre.
        let q = supabase
          .from("artwork_losses")
          .select(baseSelect)
          .eq("artwork_id", artwork.id)
          .is("recovered_at", null)
          .order("loss_date", { ascending: false })
          .limit(1);

        if (ownerClientId) q = q.eq("client_id", ownerClientId);
        queries.push(q.maybeSingle());

        // Fallback pour les anciennes déclarations sans artwork_id ou si l'id a été perdu.
        if (ownerClientId && (artwork.inventory_number || artwork.title)) {
          let fallback = supabase
            .from("artwork_losses")
            .select(baseSelect)
            .eq("client_id", ownerClientId)
            .is("recovered_at", null)
            .order("loss_date", { ascending: false })
            .limit(1);

          if (artwork.inventory_number) {
            fallback = fallback.eq("inventory_number", artwork.inventory_number);
          } else {
            fallback = fallback.eq("artwork_title", artwork.title || "");
          }

          queries.push(fallback.maybeSingle());
        }

        const results = await Promise.allSettled(queries);
        const found =
          results
            .map((r) => (r.status === "fulfilled" && !r.value?.error ? r.value?.data : null))
            .find(Boolean) || null;

        if (!cancelled) setLossRecord(found);
      } catch (err) {
        console.error("[ArtworkDetail] Erreur chargement perte/vol:", err);
        if (!cancelled) setLossRecord(null);
      }
    }

    loadLossRecord();

    return () => {
      cancelled = true;
    };
  }, [
    artwork?.id,
    artwork?.client_id,
    artwork?.owner_id,
    artwork?.inventory_number,
    artwork?.title,
  ]);

  const lossBadgeLabel = useMemo(() => {
    const type = lossRecord?.loss_type || null;
    if (!type) return null;
    return t(`artworkDetail.loss.badges.${type}`);
  }, [lossRecord?.loss_type, t]);

  const lossStatusHref = lossRecord
    ? `/pertes-vols?artwork_id=${artwork?.id || ""}`
    : "/pertes-vols";

  // Declarer perte ou vol
  async function handleDeclareLoss() {
    if (!artwork || submittingLoss) return;
    setSubmittingLoss(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const lossDate = new Date().toISOString().split("T")[0];
      const taxYear = new Date().getFullYear();
      const { data: insertedLoss, error: insertLossError } = await supabase
        .from("artwork_losses")
        .insert({
          client_id: artwork.client_id || session.user.id,
          artwork_id: artwork.id,
          artwork_title: artwork.title || "Sans titre",
          artist: artwork.artist || null,
          inventory_number: artwork.inventory_number || null,
          loss_type: lossForm.loss_type,
          loss_date: lossDate,
          tax_year: taxYear,
          declared_value: lossForm.declared_value ? Number(lossForm.declared_value) : (artwork.estimated_value || null),
          purchase_price: artwork.acquisition_price || null,
          police_report: lossForm.police_report,
          insurance_claim: lossForm.insurance_claim,
          notes: lossForm.notes || null,
          documents: [],
          created_by: session.user.id,
          updated_by: session.user.id,
        })
        .select("*")
        .maybeSingle();

      if (insertLossError) throw insertLossError;
      setLossRecord(insertedLoss || {
        client_id: artwork.client_id || session.user.id,
        artwork_id: artwork.id,
        artwork_title: artwork.title || "Sans titre",
        artist: artwork.artist || null,
        inventory_number: artwork.inventory_number || null,
        loss_type: lossForm.loss_type,
        loss_date: lossDate,
        tax_year: taxYear,
        declared_value: lossForm.declared_value ? Number(lossForm.declared_value) : (artwork.estimated_value || null),
        purchase_price: artwork.acquisition_price || null,
        police_report: lossForm.police_report,
        insurance_claim: lossForm.insurance_claim,
        notes: lossForm.notes || null,
        documents: [],
      });
      const newAvail = lossForm.loss_type === "vol" ? "stolen" : "lost";
      await supabase.from("artworks").update({ availability: newAvail }).eq("id", artwork.id);
      setArtwork(prev => ({ ...prev, availability: newAvail }));
      setShowLossModal(false);
      setLossForm({ loss_type: "vol", notes: "", declared_value: "", police_report: false, insurance_claim: false });
    } catch (err) {
      console.error("Erreur declaration perte/vol:", err);
      alert(t("artworkDetail.loss.saveError"));
    } finally {
      setSubmittingLoss(false);
    }
  }

  async function handleDelete() {

    // ✅ ne dépend pas de const canDelete déclaré plus bas (après returns)
    const canDeleteNow = !!user && isOwner && !isShared;
    if (!canDeleteNow) return;

    const ok = window.confirm(t("artworkDetail.delete.confirm"));
    if (!ok) return;

    const { error: delErr } = await supabase
      .from("artworks")
      .delete()
      .eq("id", artworkId)
      .eq("client_id", user.id);

    if (delErr) {
      console.error("Erreur suppression", delErr);
      alert(t("artworkDetail.delete.error"));
      return;
    }

    navigate("/dashboard");
  }

  // Duplication de la fiche
  const handleDuplicate = async () => {
    if (!artwork || !user) return;
    if (!window.confirm(t("artworkDetail.duplicate.confirm"))) return;
    setDuplicating(true);
    try {
      const baseInv = artwork.inventory_number || "";
      let newInvNumber = baseInv || "";
      if (baseInv) {
        const { data: existing } = await supabase
          .from("artworks")
          .select("inventory_number")
          .eq("collection_id", artwork.collection_id)
          .eq("client_id", user.id);
        const trailingNum = baseInv.match(/(-?)(\d+)$/);
        if (trailingNum) {
          const prefix = baseInv.slice(0, baseInv.length - trailingNum[0].length);
          const sep = trailingNum[1];
          const existingNums = (existing || [])
            .map(a => { const m = (a.inventory_number || "").match(/(\d+)$/); return m ? parseInt(m[1]) : 0; })
            .filter(n => !isNaN(n));
          const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : parseInt(trailingNum[2]) + 1;
          newInvNumber = prefix + sep + nextNum;
        } else {
          newInvNumber = baseInv + "-2";
        }
      }

      // Whitelist stricte = colonnes réelles de la table artworks (pas de champs virtuels/jointures)
      const ARTWORKS_COLUMNS = new Set([
        "title","artist","artist_id","year","medium","dimensions","value","image_url",
        "collection_id","location_id","client_id","description","price",
        "assigned_expert","is_public_for_sale","sale_price","provenance",
        "acquisitionDetails","documents","images","specifications","tags",
        "height","width","depth","unit","framed_height","framed_width","framed_depth",
        "paper_height","paper_width","duration","condition","conditionNotes",
        "isEdition","editionInfo","availability","subjectMatter","typeOfArt",
        "estimated_value","last_estimation_date","creationDate","proposalHistory",
        "styles","signature","signature_desc","blockchainMetadata","hash_dedupe",
        "last_edited_by","last_edited_at","artist_career_stage","artist_living",
        "artist_gender","allow_in_stats","usufruct_beneficiary_id","bare_owner_id",
        "usufruct_start_date","usufruct_end_date","is_exceptional","ownership_status",
        "currency","acquisition_date","acquisition_price","acquisition_type",
        "acquisition_source","price_low","price_high","dimensions_unit",
        "dimensions_height","dimensions_width","dimensions_depth","invoice",
        "certificate_auth","certificate_blockchain","valuation_estimated_value",
        "valuation_confidence_score","dossier_generated","valuation_market_value",
        "valuation_consensus","inventory_number","visual_analysis","color_palette",
        "composition_type","style_ai","keywords_ai","weight","weight_unit",
        "last_valuation_date","current_estimated_value","purchase_city","purchase_country",
        "certificate_authenticity","condition_report","condition_report_url",
        "dominant_colors","visual_complexity","emotional_tone","conceptual_themes",
        "narrative_content","temporal_classique","temporal_contemporain","temporal_futuriste",
        "appeal_visuel","appeal_conceptuel","appeal_emotionnel","context_intimite",
        "context_partage","market_liquidity","market_trend","market_demand",
        "normalized_style","keywords_ai_validated_at","keywords_ai_validated_by",
        "import_batch_id","import_status","primary_image_path","cup_comparables",
        "expert_note","expert_date","expert_method","lot_id","lot_copy_number",
        "valuation_low","valuation_high","valuation_method","valuation_validated_by",
        "storage_location_detail","storage_risk","visited_at","visit_risk",
        "medium_original","medium_normalized_key","medium_family_key",
        "medium_display_override","medium_normalization_status",
        "medium_normalization_confidence","medium_normalized_at","medium_normalized_by",
        "medium_category","signature_info",
      ]);
      const newArtwork = {};
      Object.keys(artwork).forEach(k => {
        if (ARTWORKS_COLUMNS.has(k)) newArtwork[k] = artwork[k];
      });
      newArtwork.inventory_number = newInvNumber;
      newArtwork.client_id = user.id;

      const { data: created, error } = await supabase
        .from("artworks")
        .insert(newArtwork)
        .select("id")
        .single();

      if (error) throw error;
      navigate("/artwork/" + created.id);
    } catch (err) {
      console.error("[Duplicate] error:", err);
      alert(t("artworkDetail.duplicate.error", { message: err.message || String(err) }));
    } finally {
      setDuplicating(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers — visuel de référence du lot
  // ─────────────────────────────────────────────────────────────────────────

  // Upload d'un visuel commun pour le lot
  // Stockage : bucket 'artworks-images' (public, comme toutes les images d'œuvres), path lots/{lotId}/reference-{ts}.{ext}
  // URL publique permanente stockée dans lots.reference_image_url (une URL signée expirerait au bout d'un an
  // et ferait disparaître l'image de toutes les œuvres du lot).
  const handleLotRefImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !artwork?.lot_id) return;
    setLotRefUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      // Path versionné par timestamp pour : (1) éviter UPDATE Storage (non autorisé par RLS),
      // (2) contourner le cache navigateur après changement.
      const timestamp = Date.now();
      const path = `lots/${artwork.lot_id}/reference-${timestamp}.${ext}`;

      // ── ÉTAPE 1 : nettoyer les anciens visuels du lot ────────────────────
      // On liste tous les fichiers commençant par "reference-" dans le dossier
      // du lot, puis on les supprime. Si la suppression échoue (RLS sur DELETE
      // restreint, fichier inexistant), on continue : l'upload du nouveau
      // fichier réussira de toute façon car le path est unique (timestamp).
      try {
        const { data: existing } = await supabase.storage
          .from("artworks-images")
          .list(`lots/${artwork.lot_id}`, { limit: 100 });
        const toDelete = (existing || [])
          .filter((f) => f.name.startsWith("reference-") || f.name.startsWith("reference."))
          .map((f) => `lots/${artwork.lot_id}/${f.name}`);
        if (toDelete.length > 0) {
          await supabase.storage.from("artworks-images").remove(toDelete);
        }
      } catch (cleanupErr) {
        // Non bloquant : on log mais on continue
        console.warn("[LotRefUpload] nettoyage anciens visuels :", cleanupErr);
      }

      // ── ÉTAPE 2 : upload du nouveau fichier (INSERT uniquement) ──────────
      const { error: upErr } = await supabase.storage
        .from("artworks-images")
        .upload(path, file, { upsert: false, contentType: file.type || "image/jpeg" });
      if (upErr) {
        throw new Error(`Upload Storage échoué : ${upErr.message}`);
      }

      // ── ÉTAPE 3 : URL publique permanente (n'expire jamais) ──────────────
      const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(path);
      const signed = { signedUrl: pub.publicUrl };

      // ── ÉTAPE 4 : mise à jour de lots.reference_image_url ────────────────
      const { error: updErr } = await supabase
        .from("lots")
        .update({ reference_image_url: signed.signedUrl })
        .eq("id", artwork.lot_id);
      if (updErr) {
        // Le fichier est uploadé mais la table n'est pas à jour : on remonte
        // l'erreur clairement pour que tu saches où regarder.
        throw new Error(`Update table lots échoué (le fichier est uploadé mais la colonne reference_image_url non mise à jour) : ${updErr.message}`);
      }

      // ── ÉTAPE 5 : refresh state local ────────────────────────────────────
      setArtwork((prev) => ({
        ...prev,
        lot: { ...(prev?.lot || {}), reference_image_url: signed.signedUrl },
      }));
    } catch (err) {
      console.error("[LotRefUpload] error:", err);
      alert(t("artworkDetail.lot.uploadError", { message: err.message || String(err) }));
    } finally {
      setLotRefUploading(false);
      // Reset input pour pouvoir ré-uploader le même fichier si besoin
      if (lotRefInputRef.current) lotRefInputRef.current.value = "";
    }
  };

  // Utiliser le visuel lot comme image principale de cette œuvre uniquement
  const handleUseLotRefAsPrimary = async () => {
    const lotRef = artwork?.lot?.reference_image_url;
    if (!lotRef || !artwork?.id) return;
    try {
      const currentImages = Array.isArray(artwork.images) ? artwork.images : [];
      // Si une image lot existe déjà, on la remplace ; sinon on l'ajoute en tête
      const withoutLotSource = currentImages.filter((i) => i?.source !== "lot");
      const newImg = {
        id: `lot-ref-${Date.now()}`,
        url: lotRef,
        isPrimary: true,
        source: "lot",
      };
      const updatedImages = [
        newImg,
        ...withoutLotSource.map((i) => (typeof i === "string" ? { url: i, isPrimary: false } : { ...i, isPrimary: false })),
      ];
      const { error } = await supabase
        .from("artworks")
        .update({ images: updatedImages })
        .eq("id", artwork.id);
      if (error) throw error;
      setArtwork((prev) => ({ ...prev, images: updatedImages }));
      setActiveImageIndex(0);
    } catch (err) {
      console.error("[UseLotRefAsPrimary] error:", err);
      alert(t("artworkDetail.lot.usePrimaryError", { message: err.message || String(err) }));
    }
  };

  // Appliquer le visuel lot comme image principale sur TOUTES les œuvres du lot
  // Boucle d'updates ; chaque œuvre conserve ses autres images en isPrimary=false
  const handleApplyLotRefToAll = async () => {
    const lotRef = artwork?.lot?.reference_image_url;
    if (!lotRef || !artwork?.lot_id) return;
    if (!window.confirm(t("artworkDetail.lot.applyConfirm"))) return;

    setApplyingToAll(true);
    setApplyToAllResult(null);
    const result = { count: 0, total: 0, errors: [] };
    try {
      // 1. Récupérer toutes les œuvres du lot avec leurs images actuelles
      const [{ data: own }, { data: shared }] = await Promise.all([
        supabase.from("artworks").select("id, inventory_number, images").eq("lot_id", artwork.lot_id),
        supabase.from("v_artworks_shared_with_me").select("id, inventory_number, images").eq("lot_id", artwork.lot_id),
      ]);
      const all = [...(own || []), ...(shared || [])];
      const unique = all.filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i);
      result.total = unique.length;

      // 2. Boucle d'updates
      for (const aw of unique) {
        const currentImages = Array.isArray(aw.images)
          ? aw.images
          : (typeof aw.images === "string" ? JSON.parse(aw.images || "[]") : []);
        const withoutLotSource = currentImages.filter((i) => i?.source !== "lot");
        const newImg = {
          id: `lot-ref-${Date.now()}-${aw.id.slice(0, 6)}`,
          url: lotRef,
          isPrimary: true,
          source: "lot",
        };
        const updatedImages = [
          newImg,
          ...withoutLotSource.map((i) =>
            typeof i === "string" ? { url: i, isPrimary: false } : { ...i, isPrimary: false }
          ),
        ];
        const { error } = await supabase
          .from("artworks")
          .update({ images: updatedImages })
          .eq("id", aw.id);
        if (error) {
          result.errors.push({ id: aw.id, inv: aw.inventory_number, message: error.message });
        } else {
          result.count += 1;
          // Si c'est l'œuvre courante, mettre à jour le state local
          if (aw.id === artwork.id) {
            setArtwork((prev) => ({ ...prev, images: updatedImages }));
            setActiveImageIndex(0);
          }
        }
      }
    } catch (err) {
      console.error("[ApplyLotRefToAll] error:", err);
      result.errors.push({ message: err.message || String(err) });
    } finally {
      setApplyingToAll(false);
      setApplyToAllResult(result);
      // Effacer le toast après 6 secondes
      setTimeout(() => setApplyToAllResult(null), 6000);
    }
  };

  // ========================================
  // 6. RETURNS CONDITIONNELS
  // ========================================
  if (isLoading) return <ArtworkDetailSkeleton />;

  if (!artwork) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-serif mb-2">{t("artworkDetail.notFound.title")}</h1>
          <p className="text-gray-600 mb-3">{t("artworkDetail.notFound.description")}</p>
          <button onClick={() => navigate("/dashboard")} className="btn-artsy btn-primary-artsy">
            {t("artworkDetail.notFound.cta")}
          </button>
        </div>
      </div>
    );
  }

  // ========================================
  // 7. VARIABLES DÉRIVÉES (après artwork)
  // ========================================
  const {
    title,
    artist,
    year,
    medium,
    dimensions,
    description,
    value,
    estimated_value,
    currency,
    isEdition,
    editionInfo,
    duration,
    typeOfArt,
    condition,
    conditionNotes,
    subjectMatter,
    inventory_number,
  } = artwork;

  // location_label : résoudre depuis la jointure locations
  const location_label_resolved =
  artwork?.locations?.name ??
  artwork?.location_label ??
  null;

  const inventoryNumber = artwork?.inventoryNumber ?? inventory_number ?? null;
  // Priorité : current_estimated_value > valuation_estimated_value > estimated_value > value
  // current_estimated_value est le champ canonique mis à jour par TOUS les workflows
  // (EstimationSemblablesPage, ArtworkEdit, RPC). valuation_estimated_value est legacy.
  // || plutôt que ?? pour sauter les zéros (oeuvre sans estimation affiche "—").
  const valeur =
    (artwork?.current_estimated_value || null) ??
    (artwork?.valuation_estimated_value || null) ??
    (estimated_value || null) ??
    (value || null) ??
    null;
  // La devise d'achat (HKD, USD…) — conservée pour info mais jamais utilisée pour l'affichage
  const currencyLabel = "EUR"; // Toutes les valeurs affichées sont en EUR
  const acquisitionCurrencyLabel = currency || "EUR"; // Devise originale d'achat
  const lang = String(i18n?.language || "fr").toLowerCase();
  const activeLocale = lang.startsWith("de")
    ? "de-DE"
    : lang.startsWith("en")
    ? "en-US"
    : "fr-FR";
  const formatMoney = (amount) => {
    if (amount === null || amount === undefined || amount === "") return null;
    const n = Number(String(amount).replace(/\s/g, "").replace(",", "."));
    if (!Number.isFinite(n)) return null;
    return n.toLocaleString(activeLocale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  };
  // Prix / galerie : champs commerciaux uniquement.
  // Ne jamais recycler valuation_low / valuation_high ici, sinon une valorisation expert
  // apparaît comme prix de vente souhaité alors que les champs galerie sont vides.
  const galleryLowLabel = formatMoney(artwork?.price_low ?? null);
  const galleryHighLabel = formatMoney(artwork?.price_high ?? null);
  const desiredSalePriceLabel = formatMoney(artwork?.sale_price ?? null);
  const hasGalleryVisibility = Boolean(galleryLowLabel || galleryHighLabel || desiredSalePriceLabel);

  // Valorisation expert : lecture seule, séparée du prix galerie.
  const expertValuationLowLabel = formatMoney(artwork?.valuation_low ?? null);
  const expertValuationHighLabel = formatMoney(artwork?.valuation_high ?? null);
  const hasExpertValuationVisibility = Boolean(expertValuationLowLabel || expertValuationHighLabel);

  const acquisitionDetails = parseMaybeJSON(artwork?.acquisitionDetails, null) || {};
  const finalAcquisitionDetails = {
    ...acquisitionDetails,
    date: acquisitionDetails.date || artwork?.acquisition_date || null,
    source: acquisitionDetails.source || artwork?.acquisition_source || null,
    type: acquisitionDetails.type || artwork?.acquisition_type || null,
  };

  // Prix d'acquisition toujours en EUR :
  // 1. acquisition_price_eur (converti depuis devise étrangère)
  // 2. acquisition_price direct si déjà en EUR
  // 3. acquisitionDetails.price en fallback
  const acquisitionPriceEur =
    artwork?.acquisition_price_eur ??
    finalAcquisitionDetails?.acquisition_price_eur ??
    null;
  const acquisitionPriceRaw =
    finalAcquisitionDetails?.price ?? artwork?.acquisition_price ?? null;
  const acquisitionPrice = acquisitionPriceEur ?? acquisitionPriceRaw;
  const acquisitionIsConverted = acquisitionPriceEur != null && acquisitionCurrencyLabel !== "EUR";

  const canEdit =
    (isOwner && !isShared) ||
    (isShared && (artwork?.can_edit === true || artwork?._canEdit === true)) ||
    (collabPerms?.can_edit === true); // collaborateur avec délégation can_edit

  const canDelete = isOwner && !isShared;

  // Actions bloquées selon le type de collection
  const isDepositCollection = artworkCollection?.collection_type === "deposit";
  const isInaliénable = ["DON_LEGS_INALIENABLE", "inalienable", "donation_inalienable"].includes(artworkCollection?.legal_status);
  const canTransact = !isDepositCollection && !isInaliénable; // vente, donation, prêt

  const docsCount = normalizeDocs(artwork?.documents).length;

  const { missing, missingCount } = countMissingCoreFields({
    location_label: location_label_resolved,
    acquisitionPrice,
    docsCount,
  });

  const nextAction = pickNextAction({ missing, canEdit });


  // (storyEvents est calculé mais pas utilisé dans ton snippet : on le garde tel quel)
  const storyEvents = buildStoryEvents({ artwork, historyItems: accessItems }); // eslint-disable-line no-unused-vars

  let plusValue = null;
  if (valeur != null && acquisitionPrice != null) {
    const toNumber = (v) => {
      if (typeof v === "number") return v;
      const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(n) ? n : null;
    };

    const valNum = toNumber(valeur);
    const acqNum = toNumber(acquisitionPrice);

    if (valNum != null && acqNum != null) {
      plusValue = valNum - acqNum;
    }
  }

  // ========================================
  // 6. RENDER PRINCIPAL
  // ========================================
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 py-4 md:py-8">
        <div className="mb-6">
          <Link
  to={
    isShared
      ? artwork?.collection_id
        ? `/expert/collections/${artwork.collection_id}`
        : "/expert/dashboard"
      : artwork?.collection_id
        ? `/collections/${artwork.collection_id}`
        : "/my-collection"
  } 	
            className="inline-flex items-center text-sm text-gray-700 hover:text-black transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("artworkDetail.backToCollection")}
          </Link>
        </div>

        {/* ── Bandeau statut En prêt / Perdue-Volée ────────────────────── */}
        {artwork?.availability === "on_loan" && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 16px", marginBottom: 16,
            background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontFamily: "system-ui", color: "#0369a1", fontWeight: 600 }}>
                ↗ {t("artworkDetail.loanBanner.status")}
              </span>
              {artwork?.storage_location_detail && (
                <span style={{ fontSize: 13, color: "#0369a1", fontFamily: "system-ui" }}>
                  · {artwork.storage_location_detail}
                </span>
              )}
            </div>
            <Link
              to="/deposits?tab=loans"
              style={{ fontSize: 12, color: "#0369a1", fontFamily: "system-ui", fontWeight: 600, textDecoration: "none" }}
            >
              {t("artworkDetail.loanBanner.manage")}
            </Link>
          </div>
        )}

        {(lossRecord || artwork?.availability === "lost_or_stolen" || artwork?.availability === "stolen" || artwork?.availability === "lost") && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 16px", marginBottom: 16,
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontFamily: "system-ui", color: "#dc2626", fontWeight: 600 }}>
                {lossBadgeLabel
                  ? `⚠ ${lossBadgeLabel}`
                  : artwork?.availability === "stolen"
                    ? `⚠ ${t("artworkDetail.loss.badges.vol")}`
                    : artwork?.availability === "lost"
                      ? `⚠ ${t("artworkDetail.loss.badges.perte")}`
                      : `⚠ ${t("artworkDetail.loss.badges.generic")}`}
              </span>
            </div>
            <Link
              to={lossStatusHref}
              style={{ fontSize: 12, color: "#dc2626", fontFamily: "system-ui", fontWeight: 600, textDecoration: "none" }}
            >
              {t("artworkDetail.loss.viewRegister")}
            </Link>
          </div>
        )}

        {artwork?.ownership_verification === "to_verify" && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            padding: "10px 16px", marginBottom: 16,
            background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6,
          }}>
            <span style={{ fontSize: 13, fontFamily: "system-ui", color: "#92400e", fontWeight: 600, flexShrink: 0 }}>
              ⚠ {t("artworkDetail.ownershipVerification.badge", { defaultValue: "Propriété à vérifier" })}
            </span>
            {artwork?.ownership_verification_note && (
              <span style={{ fontSize: 12.5, fontFamily: "system-ui", color: "#92400e", opacity: 0.9 }}>
                — {artwork.ownership_verification_note}
              </span>
            )}
          </div>
        )}

        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-serif leading-tight" style={{ fontSize: "clamp(22px, 6vw, 36px)" }}>{title || t("artworkDetail.defaultTitle")}</h1>

              {/* Œuvre numérique : catégorie de médium, informations d'œuvre numérique ou plateforme source */}
              {(artwork?.digital_asset || artwork?.external_source ||
                ["nft", "digital", "digital_art"].includes(String(artwork?.medium_category || inferMediumCategory(artwork?.medium || "")).toLowerCase())) && (
                <span
                  className="inline-flex items-center px-3 py-1 text-xs font-semibold"
                  style={{ border: "1px solid #6b5fb5", borderRadius: 2, color: "#6b5fb5", fontFamily: "system-ui" }}
                >
                  {t("artworkEdit.heritage.digitalBadge", { defaultValue: "Œuvre numérique" })}
                </span>
              )}

              {/* ✅ Badge Blockchain visible */}
              {artwork?.lot_id && (
                <div className="inline-flex items-center gap-1" style={{ border: "1px solid #c9a961", borderRadius: 2 }}>
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold cursor-pointer"
                    style={{ color: "#c9a961", fontFamily: "system-ui" }}
                    onClick={() => {
                      const lotSection = document.getElementById("lot-section");
                      if (lotSection) lotSection.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    ▣ {artwork.lot?.title || t("artworkDetail.lot.fallbackName")}
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      title={t("artworkDetail.actions.removeFromLotTitle")}
                      onClick={async () => {
                        if (!window.confirm(t("artworkDetail.actions.removeFromLotConfirm"))) return;
                        const { error } = await supabase.from("artworks").update({ lot_id: null }).eq("id", artwork.id);
                        if (!error) setArtwork(prev => ({ ...prev, lot_id: null, lot: null }));
                        else alert(t("artworkDetail.actions.removeFromLotError"));
                      }}
                      className="px-2 py-1 text-xs text-[#c9a961] hover:bg-[#c9a961]/10 transition border-l border-[#c9a961]/40"
                      style={{ fontFamily: "system-ui" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}

              {/* ── Bouton Mettre en lot (si pas encore dans un lot) ── */}
              {canEdit && !artwork?.lot_id && (
                <button
                  type="button"
                  onClick={() => setLotCreatorOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border transition hover:bg-gray-50"
                  style={{ borderColor: "#e5e3df", borderRadius: 2, fontFamily: "system-ui", color: "#555" }}
                  title={t("artworkDetail.actions.addToLotTitle")}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                    <line x1="12" y1="12" x2="12" y2="17"/>
                    <line x1="9" y1="14.5" x2="15" y2="14.5"/>
                  </svg>
                  {t("artworkDetail.actions.addToLot")}
                </button>
              )}

              {artwork?.blockchainMetadata && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-xs font-semibold">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm0-10a4 4 0 100 8 4 4 0 000-8z" />
                  </svg>
                  {t("artworkDetail.meta.blockchainCertified")}
                </span>
              )}
            </div>

            <p className="text-xl text-gray-600">
              {artist ? (
                <Link
                  to={artwork?.collection_id
                    ? `/collections/${artwork.collection_id}?artist=${encodeURIComponent(artist)}`
                    : `/my-collection?artist=${encodeURIComponent(artist)}`}
                  className="hover:underline underline-offset-2 cursor-pointer"
                  title={t("artworkDetail.viewArtistWorks", { artist })}
                >
                  {artist}
                </Link>
              ) : "—"}
              {year && ` • ${year}`}
            </p>

            {/* ✅ Badge Propriétaire/Partagée */}
            <div className="mt-2 text-xs text-gray-500">
              {isOwner && !isShared ? (
                <>{t("artworkDetail.meta.ownerStatus.owner")}</>
              ) : isOwner && isShared ? (
                <>{t("artworkDetail.meta.ownerStatus.ownerShared")}</>
              ) : isShared ? (
                <>{t("artworkDetail.meta.ownerStatus.sharedAccess")}</>
              ) : null}
            </div>
          </div>
        </div>

        {/* ✅ BOUTONS ACTIONS RAPIDES (Coûts + Conservation + Maintenance) */}
        {canEdit && (
          <div className="hidden md:flex flex-wrap gap-2 text-sm mb-6">
            <Link
              to={`/couts/${artworkId}`}
              className="inline-flex items-center px-3 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition"
            >
              <Euro className="w-4 h-4 mr-2" />
              {t("artworkDetail.actions.costs")}
            </Link>

            <Link
              to={`/conservation/${artworkId}`}
              className="inline-flex items-center px-3 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition"
            >
              <Wrench className="w-4 h-4 mr-2" />
              {t("artworkDetail.actions.conservation")}
            </Link>

            <Link
              to={`/maintenance/${artworkId}`}
              className="inline-flex items-center px-3 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition"
            >
              <ClipboardListIcon className="w-4 h-4 mr-2" />
              {t("artworkDetail.actions.maintenance")}
            </Link>

            <ArtworkDossierExport
              artwork={artwork}
              label={t("artworkDetail.actions.exportDossier")}
              className="inline-flex items-center px-3 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition text-sm"
            />
          </div>
        )}

        {/* ✅ MENU DROPDOWN (TOUTES LES AUTRES ACTIONS) */}
        <div className="flex items-center justify-end mb-8">
          <div className="relative">
            {(canEdit || canDelete || !!collabPerms) && (
              <>
                <button
                  onClick={() => setActionsOpen(!actionsOpen)}
                  className="p-2.5 rounded-full border border-gray-300 hover:bg-gray-50 transition"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {actionsOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                    {(canEdit || collabPerms?.can_edit) && (
                      <Link
                        to={`/artwork/${artworkId}/edit`}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition"
                      >
                        <Edit3 className="w-4 h-4" />
                        {t("artworkDetail.actions.edit")}
                      </Link>
                    )}

                    {isOwner && (
                      <>
                        <button
                          onClick={() => {
                            navigate(`/insurance/request/${artworkId}`);
                            setActionsOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                        >
                          <Shield className="w-4 h-4" />
                          {t("artworkDetail.actions.requestInsurance")}
                        </button>

                        <button
                          onClick={() => {
                            setActionsOpen(false);
                            navigate(`/transport/${artworkId}`);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                          title={t("artworkDetail.actions.transportTitle")}
                        >
                          <Truck className="w-4 h-4" />
                          {t("artworkDetail.actions.requestTransport")}
                        </button>

                        <button
                          onClick={() => {
                            setActionsOpen(false);
                            navigate(`/suivi-ia/${artworkId}`);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                        >
                          <TrendingUp className="w-4 h-4" />
                          {t("artworkDetail.actions.aiTracking")}
                        </button>

                        <button
                          onClick={() => {
                            setActionsOpen(false);
                            navigate("/services/valuation", { state: { preselectArtworkId: artworkId } });
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                        >
                          <Users className="w-4 h-4" />
                          <div className="flex-1">
                            <div className="font-medium">
                              {t("artworkDetail.actions.requestExpertValuation")}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {t("artworkDetail.actions.requestExpertValuationSubtext")}
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => {
                            setActionsOpen(false);
                            navigate("/services/curatorial", { state: { preselectArtworkId: artworkId } });
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                        >
                          <BookOpen className="w-4 h-4" />
                          <div className="flex-1">
                            <div className="font-medium">
                              {t("artworkDetail.actions.curatorialReading")}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {t("artworkDetail.actions.curatorialReadingSubtext")}
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => {
                            setShowQrModal(true);
                            setActionsOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                        >
                          <QrCode className="w-4 h-4" />
                          {t("artworkDetail.actions.qrCode")}
                        </button>

                        {canTransact && (
                          <button
                            onClick={() => {
                              setShowSaleModal(true);
                              setActionsOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                          >
                            <Euro className="w-4 h-4" />
                            {t("artworkDetail.actions.registerSale")}
                          </button>
                        )}

                        {canTransact && (
                          <button
                            onClick={() => {
                              setShowDonationModal(true);
                              setActionsOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                          >
                            <Gift className="w-4 h-4" />
                            {t("artworkDetail.actions.registerDonation")}
                          </button>
                        )}

                        {canTransact && (
                          <button
                            onClick={() => {
                              setShowLoanModal(true);
                              setActionsOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                            </svg>
                            {artwork?.loan_status === "on_loan" || artwork?.availability === "on_loan"
                              ? t("artworkDetail.actions.removeFromLoan")
                              : t("artworkDetail.actions.loanArtwork")}
                          </button>
                        )}

                        <button
                          onClick={() => { setShowLossModal(true); setActionsOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition text-left"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          {t("artworkDetail.actions.declareLoss")}
                        </button>

                        <button
                          onClick={() => {
                            setActionsOpen(false);
                            navigate(`/propose/${artwork.id}`);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                        >
                          <Building2Icon className="w-4 h-4" />
                          {t("artworkDetail.actions.proposeExhibition")}
                        </button>
                      </>
                    )}

                    {(isOwner || isShared) && (
                      <button
                        onClick={() => {
                          setActionsOpen(false);
                          navigate(`/estimation-semblables/${artworkId}`);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left"
                      >
                        <TrendingUp className="w-4 h-4" />
                        {t("artworkDetail.actions.estimateComparables")}
                      </button>
                    )}

                    {canEdit && (
                      <button
                        onClick={() => { setActionsOpen(false); handleDuplicate(); }}
                        disabled={duplicating}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition text-left disabled:opacity-50"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" /></svg>
                        {duplicating
                          ? t("artworkDetail.actions.duplicating")
                          : t("artworkDetail.actions.duplicateArtwork")}
                      </button>
                    )}

                    {canDelete && (
                      <button
                        onClick={() => {
                          setActionsOpen(false);
                          handleDelete();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition text-left"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t("artworkDetail.actions.delete")}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {isShared && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">{t("artworkDetail.shared.title")}</p>
              <p className="text-blue-700">{t("artworkDetail.shared.description")}</p>
            </div>
          </div>
        )}

        {/* ── Section lot : autres exemplaires ── */}
        {/* Modal LotCreator pour créer/assigner un lot depuis la fiche */}
        {lotCreatorOpen && (
          <LotCreator
            collectionId={artwork?.collection_id}
            clientId={user?.id}
            onClose={() => setLotCreatorOpen(false)}
            onCreated={() => {
              setLotCreatorOpen(false);
              // Recharger la fiche pour afficher le nouveau lot
              window.location.reload();
            }}
            preselectedArtworkId={artwork?.id}
          />
        )}

        {artwork?.lot_id && (
          <LotSiblings
            artworkId={artwork.id}
            lotId={artwork.lot_id}
            lotTitleFallback={artwork.lot?.title}
            t={t}
            canEdit={canEdit}
            collectionId={artwork.collection_id}
            clientId={artwork.client_id || user?.id}
            onDuplicate={handleDuplicate}
            duplicating={duplicating}
          />
        )}

        {/* ✅ Widget 1/3: Health check */}
        <div className="mb-6">
          <ArtworkHealthCheckBar
            t={t}
            missing={missing}
            missingCount={missingCount}
            docsCount={docsCount}
            isLost={!!(lossRecord || artwork?.availability === "lost_or_stolen" || artwork?.availability === "stolen" || artwork?.availability === "lost")}
            lossStatusHref={lossStatusHref}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div>
            <div className="bg-gray-50 rounded-xl overflow-hidden mb-4 border border-gray-200 relative">
              {activeImage ? (
                <img
                  src={activeImage}
                  alt={title || t("artworkDetail.defaultTitle")}
                  className="w-full object-contain"
                  style={{ height: "clamp(260px, 50vw, 500px)" }}
                />
              ) : artwork?.lot?.reference_image_url ? (
                // Pas d'image propre, mais un visuel lot existe → on l'affiche en placeholder doré
                <div className="relative" style={{ height: "clamp(260px, 50vw, 500px)" }}>
                  <img
                    src={artwork.lot.reference_image_url}
                    alt={t("artworkDetail.lot.referenceAlt")}
                    className="w-full h-full object-contain opacity-70"
                  />
                  <div className="absolute top-3 left-3 px-3 py-1.5 bg-white/95 border border-[#c9a961] rounded text-xs font-semibold tracking-wide" style={{ color: "#c9a961", fontFamily: "system-ui" }}>
                    ▣ {t("artworkDetail.lot.referenceLabel")}
                  </div>
                </div>
              ) : (
                <div className="w-full flex items-center justify-center" style={{ height: "clamp(260px, 50vw, 500px)" }}>
                  <ImageIcon className="w-24 h-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* ── Zone visuel lot : bandeau + actions ─────────────────────── */}
            {artwork?.lot_id && (
              <>
                {/* Bandeau visuel lot disponible (cas où l'œuvre a sa propre photo) */}
                {artwork?.lot?.reference_image_url && activeImage && artwork.lot.reference_image_url !== activeImage && (
                  <div className="mb-3 flex items-center gap-3 p-2.5 border border-[#c9a961]/40 bg-[#fffbf2] rounded-lg">
                    <img src={artwork.lot.reference_image_url} alt={t("artworkDetail.lot.referenceAlt")} className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold tracking-wide" style={{ color: "#c9a961", fontFamily: "system-ui" }}>▣ {t("artworkDetail.lot.referenceLabelUpper")}</p>
                      <p className="text-xs text-gray-500 truncate">{artwork.lot?.title || t("artworkDetail.lot.fallbackName")}</p>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={handleUseLotRefAsPrimary}
                        className="text-xs px-3 py-1.5 border border-[#c9a961] rounded hover:bg-[#c9a961]/10 transition whitespace-nowrap"
                        style={{ color: "#c9a961", fontFamily: "system-ui" }}
                        title={t("artworkDetail.lot.useAsPrimaryTitle")}
                      >
                        {t("artworkDetail.lot.useAsPrimary")}
                      </button>
                    )}
                  </div>
                )}

                {/* Cas placeholder : œuvre sans image propre, visuel lot affiché → bouton inscrire dans images */}
                {artwork?.lot?.reference_image_url && !activeImage && canEdit && (
                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={handleUseLotRefAsPrimary}
                      className="text-xs px-4 py-2 border-2 border-[#c9a961] rounded hover:bg-[#c9a961]/10 transition font-medium"
                      style={{ color: "#c9a961", fontFamily: "system-ui" }}
                    >
                      {t("artworkDetail.lot.setAsPrimary")}
                    </button>
                  </div>
                )}

                {/* Actions de gestion du visuel lot (upload / changer / appliquer à tout) */}
                {canEdit && (
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <input
                      ref={lotRefInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLotRefImageUpload}
                    />
                    <button
                      type="button"
                      onClick={() => lotRefInputRef.current?.click()}
                      disabled={lotRefUploading}
                      className="text-xs text-gray-600 border border-gray-200 px-3 py-1.5 rounded hover:border-[#c9a961] hover:text-[#c9a961] transition disabled:opacity-50"
                      style={{ fontFamily: "system-ui" }}
                      title={t("artworkDetail.lot.sharedVisualTitle")}
                    >
                      {lotRefUploading
                        ? t("artworkDetail.lot.uploading")
                        : artwork?.lot?.reference_image_url
                        ? t("artworkDetail.lot.changeVisual")
                        : t("artworkDetail.lot.addVisual")}
                    </button>

                    {artwork?.lot?.reference_image_url && (
                      <button
                        type="button"
                        onClick={handleApplyLotRefToAll}
                        disabled={applyingToAll}
                        className="text-xs px-3 py-1.5 border border-[#c9a961] rounded hover:bg-[#c9a961] hover:text-white transition disabled:opacity-50 flex items-center gap-1.5"
                        style={{ color: "#c9a961", fontFamily: "system-ui" }}
                        title={t("artworkDetail.lot.applyAllTitle")}
                      >
                        {applyingToAll ? (
                          <>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="animate-spin">
                              <line x1="12" y1="2" x2="12" y2="6"></line>
                              <line x1="12" y1="18" x2="12" y2="22"></line>
                              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                            </svg>
                            {t("artworkDetail.lot.applying")}
                          </>
                        ) : (
                          t("artworkDetail.lot.applyAll")
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Toast résultat */}
                {applyToAllResult && (
                  <div className={`mb-3 p-3 rounded text-xs border ${applyToAllResult.errors.length === 0 ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} style={{ fontFamily: "system-ui" }}>
                    {applyToAllResult.errors.length === 0
                      ? t("artworkDetail.lot.applySuccess", { count: applyToAllResult.count })
                      : t("artworkDetail.lot.applyPartial", {
                          count: applyToAllResult.count,
                          total: applyToAllResult.total,
                          errors: applyToAllResult.errors.length,
                        })}
                  </div>
                )}
              </>
            )}

            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => {
                  const src = typeof img === "string" ? img : img?.url || img?.path || null;
                  return (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden transition ${
                        idx === activeImageIndex
                          ? "border-black"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      {src ? (
                        <img src={src} alt={`${title} ${idx + 1}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* ✅ Widget 2/3: Next best action */}
            {nextAction && (
              <ArtworkNextActionCard
                t={t}
                action={nextAction}
                onGoTab={(tab) => setActiveTab(tab)}
                onGoEdit={() => navigate(`/artwork/${artworkId}/edit`)}
              />
            )}

            {/* ─── Bandeau fiche squelette (importée depuis catalogue, sans technique ni photo) ─── */}
            {canEdit && !medium && !artwork?.image_url && (
              <div style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
                padding: "14px 20px",
                borderRadius: 10,
                border: "1px solid #c9a961",
                background: "#fdf9f0",
                fontFamily: "Georgia, serif",
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: "bold", color: "#1a1a1a", marginBottom: 2 }}>
                    {t("artworkDetail.catalogImport.title")}
                  </div>
                  <div style={{ fontSize: 12, color: "#8a8a8a" }}>
                    {t("artworkDetail.catalogImport.description")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openVision(`/import-catalogue?enrich=1&artworkId=${artwork?.id}`)}
                  style={{
                    flexShrink: 0,
                    padding: "8px 18px",
                    borderRadius: 6,
                    background: "#c9a961",
                    color: "#fff",
                    fontSize: 12,
                    letterSpacing: "0.08em",
                    fontFamily: "Georgia, serif",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {t("artworkDetail.catalogImport.enrich")}
                </button>
              </div>
            )}

            {/* ── Bloc prix : global (installation) ou par unité ── */}
            {artwork?.lot?.pricing_mode === "global" ? (
              <div className="rounded-xl border p-5 bg-[#fffbf2]" style={{ borderColor: "#c9a961" }}>
                <div className="flex items-center gap-2 text-sm mb-2" style={{ color: "#c9a961" }}>
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 11 }}>{t("artworkDetail.lot.globalPriceTitle")}</span>
                </div>
                <p className="text-2xl font-bold" style={{ color: "#1a1a1a" }}>
                  {artwork.lot.global_price != null
                    ? Number(artwork.lot.global_price).toLocaleString(activeLocale, { style: "currency", currency: "EUR" })
                    : "—"}
                </p>
                <p className="text-xs mt-2" style={{ color: "#999" }}>
                  {t("artworkDetail.lot.globalPriceDescription", {
                    count: artwork.lot.total_copies || "?",
                  })}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                    <TrendingUp className="w-4 h-4" />
                    {t("artworkDetail.valuations.current")}
                  </div>
                  <p className="text-2xl font-bold">
                    {valeur != null
                      ? Number(valeur).toLocaleString(activeLocale, {
                          style: "currency",
                          currency: currencyLabel,
                        })
                      : "—"}
                  </p>
                  {plusValue != null && (
                    <p className={`text-sm mt-1 font-medium ${plusValue >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {plusValue >= 0 ? "+" : ""}
                      {Number(plusValue).toLocaleString(activeLocale, {
                        style: "currency",
                        currency: currencyLabel,
                      })}
                    </p>
                  )}
                </div>

                {acquisitionPrice != null && (
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                      <HandCoinsIcon className="w-4 h-4" />
                      {t("artworkDetail.acquisition.price")}
                    </div>
                    <p className="text-2xl font-bold">
                      {Number(acquisitionPrice).toLocaleString(activeLocale, {
                        style: "currency",
                        currency: "EUR",
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    {acquisitionIsConverted && acquisitionPriceRaw != null && (
                      <p className="text-xs text-gray-400 mt-1">
                        {Number(acquisitionPriceRaw).toLocaleString(activeLocale, {
                          maximumFractionDigits: 0,
                        })}{" "}{acquisitionCurrencyLabel}{" "}{t("artworkDetail.pricing.atPurchaseRate")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                <Truck className="w-4 h-4" />
                {t("artworkDetail.details.location")}
              </div>

              <p className="text-lg font-medium">
                {location_label_resolved ? (
                  location_label_resolved
                ) : canEdit ? (
                  <Link to={`/artwork/${artworkId}/edit`} className="text-blue-600 hover:underline">
                    {t("artworkDetail.locationMissingCta")}
                  </Link>
                ) : (
                  <span className="text-gray-400 italic">{t("artworkDetail.locationMissingReadonly")}</span>
                )}
              </p>
            </div>
{canEdit && (() => {
  // Raison précise du blocage IA
  const ocrBlocked = !hasPaidPlanForAI
    ? { reason: "plan", messageKey: "artworkDetail.ocr.blockedPlan" }
    : !hasAIConsent
    ? { reason: "consent", messageKey: "artworkDetail.ocr.blockedConsent" }
    : !usesAI
    ? { reason: "disabled", messageKey: "artworkDetail.ocr.blockedDisabled" }
    : null;

  if (!ocrBlocked) {
    // ✅ autorisé
    return (
      <div ref={ocrLauncherRef}>
        <OcrEnrichButton
          artwork={artwork}
          onEnriched={(patch) => setArtwork((a) => ({ ...a, ...patch }))}
          label={t("artworkDetail.ocr.button")}
          loadingLabel={t("artworkDetail.ocr.loading")}
        />
      </div>
    );
  }

  // ⛔ bloqué — bouton grisé + message contextuel
  return (
    <div style={{
      border: "1px solid #e5e3df",
      borderRadius: 8,
      padding: "12px 16px",
      background: "#fafaf8",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
    }}>
      <div style={{ fontSize: 18, lineHeight: 1 }}>✨</div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#6b6b6b",
          marginBottom: 4,
          fontFamily: "system-ui, sans-serif",
          letterSpacing: "0.03em",
        }}>
          {t("artworkDetail.ocr.button")}
        </div>
        <div style={{ fontSize: 12, color: "#8a8a8a", fontFamily: "system-ui, sans-serif", lineHeight: 1.5 }}>
          {t(ocrBlocked.messageKey)}
          {ocrBlocked.reason === "plan" && (
            <a
              href="/upgrade-downgrade"
              style={{ display: "inline-block", marginLeft: 6, color: "#c9a961", textDecoration: "underline", fontSize: 12 }}
            >
              {t("artworkDetail.ocr.viewPlans")}
            </a>
          )}
          {ocrBlocked.reason === "consent" && (
            <a
              href="/profile"
              style={{ display: "inline-block", marginLeft: 6, color: "#c9a961", textDecoration: "underline", fontSize: 12 }}
            >
              {t("artworkDetail.ocr.profile")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
})()}

            {canUseAI && (
              <div style={{
                border: "1px solid #e5e3df",
                borderRadius: 6,
                padding: "14px 16px",
                background: "#fafaf8",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9a961" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 19.5h18M3.75 4.5h16.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V5.25a.75.75 0 01.75-.75z"/></svg>
                  <div>
                    <div style={{ fontSize: 12, fontFamily: "system-ui", fontWeight: 600, color: "#1a1a1a", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 2 }}>
                      {t("artworkDetail.visualAi.title")}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui" }}>
                      {t("artworkDetail.visualAi.description")}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openVision(`/visual-analysis?artworkId=${artwork?.id}`)}
                  style={{
                    padding: "7px 14px", fontSize: 12, fontFamily: "system-ui", fontWeight: 500,
                    background: "#1a1a1a", border: "1px solid #1a1a1a", borderRadius: 4,
                    color: "#fff", cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {t("artworkDetail.visualAi.launch")}
                </button>
              </div>
            )}

            {inventoryNumber && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">{t("artworkDetail.inventoryNumber")}:</span>{" "}
                {inventoryNumber}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div
            className="flex gap-1 overflow-x-auto"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
          >
            {[
              ["details", "artworkDetail.tabs.details"],
              ["lifecycle", "artworkDetail.tabs.lifecycle", "Vie de l'œuvre"],
              ["location", "artworkDetail.tabs.location"],
              ["specifications", "artworkDetail.tabs.specifications"],
              ["provenance", "artworkDetail.tabs.provenance", "Provenance & acquisition"],
              ["documents", "artworkDetail.tabs.documents"],
              ["medias", "artworkDetail.tabs.images"],
            ].map(([tab, key, fallback]) => (
              <button
                key={tab}
                className={`pb-3 px-2 border-b-2 whitespace-nowrap transition text-sm ${
                  activeTab === tab
                    ? "border-black text-black font-medium"
                    : "border-transparent text-gray-500 hover:text-black"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {t(key, fallback ? { defaultValue: fallback } : undefined)}
              </button>
            ))}

            {isOwner && (
              <button
                className={`pb-3 px-2 border-b-2 whitespace-nowrap transition text-sm ${
                  activeTab === "history"
                    ? "border-black text-black font-medium"
                    : "border-transparent text-gray-500 hover:text-black"
                }`}
                onClick={() => setActiveTab("history")}
              >
                {t("artworkDetail.tabs.history")}
              </button>
            )}

            {isOwner && (
              <button
                className={`pb-3 px-2 border-b-2 whitespace-nowrap transition text-sm ${
                  activeTab === "access"
                    ? "border-black text-black font-medium"
                    : "border-transparent text-gray-500 hover:text-black"
                }`}
                onClick={() => setActiveTab("access")}
              >
                {t("artworkDetail.tabs.access")}
              </button>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {activeTab === "details" && (
            <div className="prose prose-sm max-w-none">
              <ArtworkReadinessCard artwork={artwork} ownerName={isOwner ? `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() : ""} className="not-prose mb-6" />
              {(ownershipHolders.bareOwnerName || ownershipHolders.usufructName) && (
                <div className="not-prose mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ownershipHolders.bareOwnerName && (
                    <OwnershipHint ownership_status="bare_ownership" holderName={ownershipHolders.bareOwnerName} />
                  )}
                  {ownershipHolders.usufructName && (
                    <OwnershipHint ownership_status="usufruct" holderName={ownershipHolders.usufructName} />
                  )}
                </div>
              )}
              {description ? (
                <p className="text-gray-700 leading-relaxed">{description}</p>
              ) : (
                <div className="text-sm text-gray-500">
                  <p>{t("artworkDetail.noDescription")}</p>
                  {canEdit && (
                    <Link
                      to={`/artwork/${artworkId}/edit`}
                      className="inline-block mt-2 text-blue-600 hover:underline"
                    >
                      {t("artworkDetail.noDescriptionCta")}
                    </Link>
                  )}
                </div>
              )}

              <ArtworkValuationHistory
                artworkId={artworkId}
                canEdit={canEdit}
                defaultCurrency={artwork?.currency || "EUR"}
              />

              <dl className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {dimensions && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.dimensions")}</dt>
                    <dd className="font-medium text-gray-900">{dimensions}</dd>
                  </div>
                )}
                {medium && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.mediumLabel")}</dt>
                    <dd className="font-medium text-gray-900">{mediumDisplayText(artwork, t, lang)}</dd>
                  </div>
                )}
                {(artwork?.posthumous_production === true || artwork?.production_cost != null) && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.production", { defaultValue: "Production" })}</dt>
                    <dd className="font-medium text-gray-900">
                      {[
                        artwork?.posthumous_production === true
                          ? [t("artworkDetail.meta.posthumous", { defaultValue: "Production posthume" }), artwork?.posthumous_production_year,
                             artwork?.posthumous_authorized_by ? t("artworkDetail.meta.authorizedBy", { defaultValue: "autorisée par {{who}}", who: artwork.posthumous_authorized_by }) : null].filter(Boolean).join(" · ")
                          : null,
                        artwork?.production_cost != null
                          ? t("artworkDetail.meta.productionCost", { defaultValue: "Frais : {{amount}}", amount: `${Number(artwork.production_cost).toLocaleString(String(lang).startsWith("fr") ? "fr-FR" : lang)} ${artwork.production_cost_currency || "EUR"}` })
                          : null,
                      ].filter(Boolean).join(" — ")}
                    </dd>
                  </div>
                )}
                {typeOfArt && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.typeOfArt")}</dt>
                    <dd className="font-medium text-gray-900">{getTypeOfArtLabel(typeOfArt, t)}</dd>
                  </div>
                )}
                {subjectMatter && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.subjectMatter")}</dt>
                    <dd className="font-medium text-gray-900">{subjectMatter}</dd>
                  </div>
                )}

                {hasGalleryVisibility && (
                  <div className="md:col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <dt className="text-gray-500 mb-2">{t("artworkEdit.detail.priceGallery")}</dt>
                    <dd className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {galleryLowLabel && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t("artworkEdit.detail.priceLow")}</div>
                          <div className="font-medium text-gray-900">{galleryLowLabel}</div>
                        </div>
                      )}
                      {galleryHighLabel && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t("artworkEdit.detail.priceHigh")}</div>
                          <div className="font-medium text-gray-900">{galleryHighLabel}</div>
                        </div>
                      )}
                      {desiredSalePriceLabel && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t("artworkEdit.detail.salePriceGallerySlash")}</div>
                          <div className="font-semibold text-gray-900">{desiredSalePriceLabel}</div>
                        </div>
                      )}
                    </dd>
                  </div>
                )}

                {hasExpertValuationVisibility && (
                  <div className="md:col-span-2 rounded-xl border border-gray-200 bg-white p-4">
                    <dt className="text-gray-500 mb-2">{t("artworkEdit.detail.expertValuation")}</dt>
                    <dd className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {expertValuationLowLabel && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t("artworkEdit.detail.priceLow")}</div>
                          <div className="font-medium text-gray-900">{expertValuationLowLabel}</div>
                        </div>
                      )}
                      {expertValuationHighLabel && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{t("artworkEdit.detail.priceHigh")}</div>
                          <div className="font-medium text-gray-900">{expertValuationHighLabel}</div>
                        </div>
                      )}
                    </dd>
                  </div>
                )}

                {duration && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.duration")}</dt>
                    <dd className="font-medium text-gray-900">{duration}</dd>
                  </div>
                )}
                {condition && (
                  <div>
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.condition")}</dt>
                    <dd className="font-medium text-gray-900">
                      {(() => {
                        const normalizedCondition = String(condition || "")
                          .toLowerCase()
                          .replace(/\s+/g, "_");
                        const legacyMap = {
                          tres_moyen: "very_poor",
                          tres_abime_restaurable: "heavily_damaged",
                          partiellement_restaurable: "partially_restorable",
                        };
                        const conditionKey = legacyMap[normalizedCondition] || normalizedCondition;
                        return t(`artwork.condition.${conditionKey}`, { defaultValue: condition });
                      })()}
                    </dd>
                  </div>
                )}
                {conditionNotes && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.conditionNotes")}</dt>
                    <dd className="font-medium text-gray-900 whitespace-pre-wrap">{conditionNotes}</dd>
                  </div>
                )}
                {isEdition && editionInfo && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 mb-1">{t("artworkDetail.meta.edition")}</dt>
                    <dd className="font-medium text-gray-900">{editionInfo}</dd>
                  </div>
                )}
              </dl>

              {safeTags.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {safeTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* ✅ Section détail technique de la preuve d'authenticité */}
              {artwork.blockchainMetadata && (
                <section className="mt-6 rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">{t("artworkDetail.blockchain.title")}</h3>
                  <dl className="space-y-1 text-xs text-gray-700">
                    {artwork.blockchainMetadata.type && (
                      <div className="flex gap-1">
                        <dt className="font-medium">{t("artworkDetail.blockchain.type")}</dt>
                        <dd>{artwork.blockchainMetadata.type}</dd>
                      </div>
                    )}
                    {artwork.blockchainMetadata.blockchain && (
                      <div className="flex gap-1">
                        <dt className="font-medium">{t("artworkDetail.blockchain.blockchain")}</dt>
                        <dd>{artwork.blockchainMetadata.blockchain}</dd>
                      </div>
                    )}
                    {artwork.blockchainMetadata.contractAddress && (
                      <div className="flex gap-1">
                        <dt className="font-medium">{t("artworkDetail.blockchain.contract")}</dt>
                        <dd className="break-all">{artwork.blockchainMetadata.contractAddress}</dd>
                      </div>
                    )}
                    {artwork.blockchainMetadata.tokenId && (
                      <div className="flex gap-1">
                        <dt className="font-medium">{t("artworkDetail.blockchain.tokenId")}</dt>
                        <dd>{artwork.blockchainMetadata.tokenId}</dd>
                      </div>
                    )}
                    {artwork.blockchainMetadata.explorerUrl && (
                      <div className="flex gap-1">
                        <dt className="font-medium">{t("artworkDetail.blockchain.link")}</dt>
                        <dd>
                          <a
                            href={artwork.blockchainMetadata.explorerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {t("artworkDetail.blockchain.linkLabel")}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </section>
              )}
            </div>
          )}

          {activeTab === "medias" && (
            <LocalBoundary name="ArtworkImages (medias)">
              {/* ⚠️ Choisis UNE des 2 lignes selon le composant ArtworkImages */}
              <ArtworkImages images={images} readOnly />
              {/* ou si ton composant attend artwork : <ArtworkImages artwork={artwork} readOnly /> */}
            </LocalBoundary>
          )}

          {activeTab === "specifications" && (
            <LocalBoundary name="ArtworkSpecificationsTab">
              <ArtworkSpecificationsTab formData={artwork} readOnly={!canEdit} />
            </LocalBoundary>
          )}

          {activeTab === "provenance" && (
            <LocalBoundary name="ArtworkProvenanceAcquisitionTab">
              <ArtworkProvenanceAcquisitionTab
                artwork={artwork}
                formData={artwork}
                setFormData={(updated) => setArtwork(updated)}
                readOnly={true}
              />
            </LocalBoundary>
          )}

          {activeTab === "documents" && (
            <LocalBoundary name="ArtworkDocumentsTab">
              <ArtworkDocumentsTab
	       formData={artwork}
	       setFormData={(updated) => setArtwork(updated)}		
	       readOnly={!canEdit}
	       canReclassify={true}
	      />
            </LocalBoundary>
          )}

          {activeTab === "lifecycle" && (
            <LocalBoundary name="ArtworkLifecycleTab">
              <ArtworkLifecycleTab artworkId={artwork?.id} locale={activeLocale} canEdit={canEdit} />
            </LocalBoundary>
          )}

          {activeTab === "location" && (
            <LocalBoundary name="ArtworkLocationTab">
              <ArtworkLocationTab
  formData={{
    ...artwork,
    location_label: location_label_resolved,
    storage_location_detail:
      canEdit
        ? (artwork?.storage_location_detail_visible ?? artwork?.storage_location_detail ?? null)
        : null,
  }}
  readOnly={!canEdit}
  onChangeLocation={() =>
    navigate(
      isShared
        ? `/expert/artwork/${artworkId}/edit-location`
        : `/artwork/${artworkId}/edit`
    )
  }
  locations={
    location_label_resolved
      ? [
          {
            id: artwork?.location_id ?? "shared-location",
            name: location_label_resolved,
          },
        ]
      : []
  }
/>
            </LocalBoundary>
          )}

          {activeTab === "history" && isOwner && (
            <LocalBoundary name="History">
              <HistoryTabContent 
                t={t}
                recordItems={recordItems}
                recordLoading={recordLoading}
                currentUserId={user?.id}
                locale={activeLocale}
              />
            </LocalBoundary>
          )}

          {activeTab === "access" && isOwner && (
            <LocalBoundary name="Access">
              <div className="space-y-8">
                {/* Section Blockchain */}
                <div>
                  <LocalBoundary name="BlockchainCertificate">
                    <BlockchainCertificate artwork={artwork} profile={profile} />
                  </LocalBoundary>
                </div>

                {/* Section Gestion des accès */}
                <div>
                  <LocalBoundary name="AccessManagement">
                    <ArtworkAccessManagement
                      t={t}
                      items={accessItems}
                      loading={accessLoading}
                      artworkId={artwork.id}
                      onAccessChange={refetchAccess}
                    />
                  </LocalBoundary>
                </div>
              </div>
            </LocalBoundary>
          )}
        </div>
      </div>

      {/* Modals */}
      {showSaleModal && (
        <SaleModal
          artwork={artwork}
          onClose={() => setShowSaleModal(false)}
          onSaved={() => setShowSaleModal(false)}
        />
      )}

      {showDonationModal && (
        <DonationModal
          artwork={artwork}
          onClose={() => setShowDonationModal(false)}
          onSaved={() => setShowDonationModal(false)}
        />
      )}

      {showLoanModal && (
        <LoanModal
          artwork={artwork}
          onClose={() => setShowLoanModal(false)}
          onSaved={() => { setShowLoanModal(false); window.location.reload(); }}
        />
      )}

      {/* ===== Modale Perte / Vol ===== */}
      {showLossModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h2 className="text-lg font-serif text-gray-900">{t("artworkDetail.loss.modal.title")}</h2>
              </div>
              <button onClick={() => setShowLossModal(false)} className="text-gray-400 hover:text-black text-2xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <strong>{t("artworkDetail.loss.modal.artwork")}:</strong>{" "}
                {artwork?.inventory_number ? artwork.inventory_number + " — " : ""}
                {artwork?.title || t("artworkDetail.defaultTitle")}
                {artwork?.artist ? " — " + artwork.artist : ""}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{t("artworkDetail.loss.modal.type")}</label>
                <select
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-black"
                  value={lossForm.loss_type}
                  onChange={e => setLossForm(f => ({ ...f, loss_type: e.target.value }))}
                >
                  <option value="vol">{t("artworkDetail.loss.types.vol")}</option>
                  <option value="perte">{t("artworkDetail.loss.types.perte")}</option>
                  <option value="destruction">{t("artworkDetail.loss.types.destruction")}</option>
                  <option value="incendie">{t("artworkDetail.loss.types.incendie")}</option>
                  <option value="inondation">{t("artworkDetail.loss.types.inondation")}</option>
                  <option value="autre">{t("artworkDetail.loss.types.autre")}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{t("artworkDetail.loss.modal.declaredValue")}</label>
                <input
                  type="number" min="0"
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-black"
                  placeholder={artwork?.estimated_value ? String(artwork.estimated_value) : t("artworkDetail.loss.modal.estimatedValue")}
                  value={lossForm.declared_value}
                  onChange={e => setLossForm(f => ({ ...f, declared_value: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-black"
                    checked={lossForm.police_report}
                    onChange={e => setLossForm(f => ({ ...f, police_report: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">{t("artworkDetail.loss.modal.policeReport")}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-black"
                    checked={lossForm.insurance_claim}
                    onChange={e => setLossForm(f => ({ ...f, insurance_claim: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">{t("artworkDetail.loss.modal.insuranceClaim")}</span>
                </label>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{t("artworkDetail.loss.modal.notes")}</label>
                <textarea rows={3}
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded resize-none focus:outline-none focus:border-black"
                  placeholder={t("artworkDetail.loss.modal.notesPlaceholder")}
                  value={lossForm.notes}
                  onChange={e => setLossForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <p className="text-xs text-gray-400">
                {t("artworkDetail.loss.modal.notice", {
                  status:
                    lossForm.loss_type === "vol"
                      ? t("artworkDetail.loss.types.vol")
                      : t("artworkDetail.loss.types.perte"),
                })}
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowLossModal(false)} className="btn-artsy btn-secondary-artsy">{t("artworkDetail.loss.modal.cancel")}</button>
                <button
                  type="button"
                  onClick={handleDeclareLoss}
                  disabled={submittingLoss}
                  style={{ background: submittingLoss ? "#9ca3af" : "#991b1b" }}
                  className="px-5 py-2 text-sm font-medium text-white rounded disabled:opacity-50"
                >
                  {submittingLoss
                    ? t("artworkDetail.loss.modal.saving")
                    : t("artworkDetail.loss.modal.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <h2 className="text-xl font-serif mb-4">{t("artworkDetail.qr.title")}</h2>
            <div className="flex justify-center mb-4 bg-white p-4 rounded-lg border border-gray-200">
              <QRCodeCanvas
                value={`${window.location.origin}/artwork/${artworkId}`}
                size={200}
                level="H"
                includeMargin
              />
            </div>
            <p className="text-xs text-gray-500 mb-4">{t("artworkDetail.qr.scanText")}</p>
            <button
              onClick={() => setShowQrModal(false)}
              className="px-6 py-2.5 text-sm rounded-full border border-gray-300 hover:bg-gray-50 transition font-medium"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}